import { useEffect, useRef, useState } from "react";
import { withInboundProcessGuard } from "src/hocs/with-role-based-guard";
import { InboundRequestViewEmailHistoryModal } from "src/pages/dashboard/inbound-request/modals/inbound-request-view-email-history-modal";
import { paths } from "src/paths";
import {
  Autocomplete,
  Avatar,
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Container,
  Card,
  Divider,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  useMediaQuery,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Seo } from "src/components/seo";
import {
  CustomTextField,
  CustomTypography,
  CustomButton,
  CustomFormControl,
} from "src/components/inputs";
// Redux Toolkit
import { thunks } from "src/thunks/inbound-process";
import { thunks as assetsThunks } from "src/thunks/assets";
import { useDispatch, useSelector } from "src/store";
import { useAuth } from "src/hooks/use-auth";
import {
  CloseOutlined,
  Error,
  KeyboardBackspace,
  Clear as ClearIcon,
  PlaylistAdd,
  PhotoCamera as PhotoCameraIcon,
  GetApp,
  FileUpload,
  ContactEmergency as ContactEmergencyIcon,
} from "@mui/icons-material";
import { getInboundStatusType } from "./inbound-table-settings";
import { groupTypes } from "src/guards/role-based-guard";
import moment from "moment";
// Icons
import UndoIcon from "@mui/icons-material/Undo";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RouteIcon from "@mui/icons-material/Route";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import * as XLSX from "xlsx";
import camelCase from "lodash.camelcase";
import toast from "react-hot-toast";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CustomCalendarIcon from "src/icons/tmw-ui/tmw-Calendar-Symbol-Option-2.png";
import tmwWarningIcon from "src/icons/tmw-ui/tmw-warning-icon.png";
import Collapse from "@mui/material/Collapse";
import CardContent from "@mui/material/CardContent";
import { styled } from "@mui/material/styles";
import { PropertyList } from "src/components/property-list";
import { PropertyListItem } from "src/components/property-list-item";

const EditInboundRequestPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const params = useParams();
  const selectPOModalRef = useRef(null);
  const zedonkFileInputRef = useRef(null);
  const inputRef = useRef();

  const userGroup = user?.group?.key;

  // Params
  const inboundRequestId = params.inbound_request_id;

  const {
    inboundRequest,
    inboundRequestLoading,
    POItems,
    POItemsLoading,
    submitInboundRequestLoading,
    submitInboundRequestError,
    saveChangesLoading,
    submitCurrencyError,
    inboundRequestStatusChangeSubmitLoading,
    inboundRequestStatusChangeDraftLoading,
    inboundRequestStatusChangePendingLoading,
    inboundRequestStatusChangeApproveLoading,
    inboundRequestsItemsQuantityOfPO,
    pickupAutocompleteData,
    supplierAddresses,
    supplierAddressesLoading,
    inboundRequestEmailHistory,
  } = useSelector((state) => state.inboundProcess);

  const [selectedPOItems, setSelectedPOItems] = useState([]);
  const [
    selectPurchaseOrderItemsModalVisible,
    setSelectPurchaseOrderItemsModalVisible,
  ] = useState(false);
  const [currentModalSection, setCurrentModalSection] =
    useState("remaining_fields");
  const [quantityValues, setQuantityValues] = useState({});
  const [firstQuantityValues, setFirstQuantityValues] = useState({});
  const [originalPoItemsQuantities, setOriginalPoItemsQuantities] = useState(
    [],
  );
  const [extraUnitsModalOpened, setExtraUnitsModalOpened] = useState(false);
  const [extraUnitsReceivedValues, setExtraUnitsReceivedValues] = useState({});
  const [extraUnitsReceivedChanged, setExtraUnitsReceivedChanged] =
    useState(false);
  const [fieldsChanged, setFieldsChanged] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [notesModalAction, setNotesModalAction] = useState("draft"); // "draft" or "pending"
  const [draftNotes, setDraftNotes] = useState("");
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [emailHistoryModalVisible, setEmailHistoryModalVisible] =
    useState(false);
  const [invoiceNetTotal, setInvoiceNetTotal] = useState("");
  const [invoiceNetTotalError, setInvoiceNetTotalError] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [incotermError, setIncotermError] = useState("");
  const [confirmIRModalOpened, setConfirmIRModalOpened] = useState(false);
  const [extraUnitsConfirmModalOpened, setExtraUnitsConfirmModalOpened] =
    useState(false);
  const [approveIRModalOpened, setApproveIRModalOpened] = useState(false);
  const [closeIRModalOpened, setCloseIRModalOpened] = useState(false);
  const [closeInboundRequestLoading, setCloseInboundRequestLoading] =
    useState(false);
  const [closeInboundRequestAction, setCloseInboundRequestAction] =
    useState("close");
  const [notifyChangeRequestModalOpened, setNotifyChangeRequestModalOpened] =
    useState(false);
  const [changeRequestNote, setChangeRequestNote] = useState("");
  const [notifyChangeRequestLoading, setNotifyChangeRequestLoading] =
    useState(false);
  const [missingItemsChoiceModalVisible, setMissingItemsChoiceModalVisible] =
    useState(false);
  const [uploadCNForMissingItemsChoice, setUploadCNForMissingItemsChoice] =
    useState(false);
  const [shipMissingItemsChoice, setShipMissingItemsChoice] = useState(false);
  const [missingItemsCreditNoteFileName, setMissingItemsCreditNoteFileName] =
    useState("");
  const [missingItemsCreditNoteFileData, setMissingItemsCreditNoteFileData] =
    useState(null);
  const [
    saveMissingItemsCreditNoteLoading,
    setSaveMissingItemsCreditNoteLoading,
  ] = useState(false);
  const [
    confirmMissingItemsChoiceLoading,
    setConfirmMissingItemsChoiceLoading,
  ] = useState(false);
  const [selectAllItemsChecked, setSelectAllItemsChecked] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [searchPOItem, setSearchPOItem] = useState("");
  const [filteredPOItems, setFilteredPOItems] = useState([]);
  const [importZedonkParsing, setImportZedonkParsing] = useState(false);
  const [shortShipmentAnswers, setShortShipmentAnswers] = useState({});
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [poSummaryExpanded, setPoSummaryExpanded] = useState(false);

  const [pickupDetailsChanged, setPickupDetailsChanged] = useState(false);
  // Pickup Fields
  const [pickupConfirmationNumber, setPickupConfirmationNumber] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState(new Date());
  const [numberOfBoxes, setNumberOfBoxes] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(new Date());
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryNumber, setDeliveryNumber] = useState("");
  const [totalNumberOfCartons, setTotalNumberOfCartons] = useState("");
  const [totalNumberOfPallets, setTotalNumberOfPallets] = useState("");
  const [boxDimensions, setBoxDimensions] = useState("");
  const [palletDimensions, setPalletDimensions] = useState("");
  const [totalGrossWeight, setTotalGrossWeight] = useState("");
  const [totalGrossWeightError, setTotalGrossWeightError] = useState("");
  const [totalNetWeight, setTotalNetWeight] = useState("");
  const [totalNetWeightError, setTotalNetWeightError] = useState("");
  const [pickupZipCode, setPickupZipCode] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [pickupCountry, setPickupCountry] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupCompanyName, setPickupCompanyName] = useState("");
  const [openingHours, setOpeningHours] = useState("");
  const [contactName, setContactName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [supplierEmailContact, setSupplierEmailContact] = useState("");
  const [contentDetails, setContentDetails] = useState("");
  const [packingListNumber, setPackingListNumber] = useState("");
  const [comments, setComments] = useState("");
  const [readyDateForPickup, setReadyDateForPickup] = useState("");
  // Total units (qty) input and validation
  const [totalUnits, setTotalUnits] = useState("");
  const [totalUnitsError, setTotalUnitsError] = useState("");

  const [notesSystemModalVisible, setNotesSystemModalVisible] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteAttachment, setNewNoteAttachment] = useState(null);
  const [newNoteAttachmentName, setNewNoteAttachmentName] = useState("");
  const [addNoteLoading, setAddNoteLoading] = useState(false);
  const [draftAttachment, setDraftAttachment] = useState(null);
  const [draftAttachmentName, setDraftAttachmentName] = useState("");
  const [notesView, setNotesView] = useState("list");
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [editNoteAttachment, setEditNoteAttachment] = useState(null);
  const [editNoteAttachmentName, setEditNoteAttachmentName] = useState("");
  const [editNoteLoading, setEditNoteLoading] = useState(false);
  const [deleteNoteModalVisible, setDeleteNoteModalVisible] = useState(false);
  const [isEditingAuthorized, setIsEditingAuthorized] = useState(false);
  // Approval modal fields
  const [inbshipRequestDate, setInbshipRequestDate] = useState(null);
  const [approvalMemo, setApprovalMemo] = useState("");

  const poIncotermFromPO =
    inboundRequest?.purchase_order?.incoterm || inboundRequest?.incoterm || "";
  const isDDPIncoterm = poIncotermFromPO === "DDP";

  const totalGrossWeightValidation = (value, netWeight = totalNetWeight) => {
    const raw = "" + (value ?? "");
    const normalized = raw.replace(",", ".");
    setTotalGrossWeight(normalized);

    if (!normalized) {
      setTotalGrossWeightError("Total Gross Weight is required.");
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      setTotalGrossWeightError(
        "Invalid number. Use '.' for decimals (e.g., 1234.56).",
      );
    } else {
      const grossNum = parseFloat(normalized);
      const netNum = parseFloat(netWeight?.replace(",", ".") || "0");
      if (netWeight && !isNaN(netNum) && grossNum < netNum) {
        setTotalGrossWeightError("Gross Weight must be >= Net Weight.");
      } else {
        setTotalGrossWeightError("");
      }
    }

    // Also revalidate net weight when gross changes
    if (netWeight && !/^\d+(?:\.\d{1,2})?$/.test(netWeight.replace(",", "."))) {
      // Net weight has its own format error, don't change it
    } else if (netWeight) {
      const grossNum = parseFloat(normalized || "0");
      const netNum = parseFloat(netWeight.replace(",", ".") || "0");
      if (!isNaN(grossNum) && !isNaN(netNum) && grossNum < netNum) {
        setTotalNetWeightError("Net Weight must be <= Gross Weight.");
      } else if (
        totalNetWeightError === "Net Weight must be <= Gross Weight."
      ) {
        setTotalNetWeightError("");
      }
    }
  };

  const totalNetWeightValidation = (value, grossWeight = totalGrossWeight) => {
    const raw = "" + (value ?? "");
    const normalized = raw.replace(",", ".");
    setTotalNetWeight(normalized);

    if (!normalized) {
      setTotalNetWeightError("Total Net Weight is required.");
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      setTotalNetWeightError(
        "Invalid number. Use '.' for decimals (e.g., 1234.56).",
      );
    } else {
      const netNum = parseFloat(normalized);
      const grossNum = parseFloat(grossWeight?.replace(",", ".") || "0");
      if (grossWeight && !isNaN(grossNum) && netNum > grossNum) {
        setTotalNetWeightError("Net Weight must be <= Gross Weight.");
      } else {
        setTotalNetWeightError("");
      }
    }

    // Also revalidate gross weight when net changes
    if (
      grossWeight &&
      !/^\d+(?:\.\d{1,2})?$/.test(grossWeight.replace(",", "."))
    ) {
      // Gross weight has its own format error, don't change it
    } else if (grossWeight) {
      const netNum = parseFloat(normalized || "0");
      const grossNum = parseFloat(grossWeight.replace(",", ".") || "0");
      if (!isNaN(grossNum) && !isNaN(netNum) && grossNum < netNum) {
        setTotalGrossWeightError("Gross Weight must be >= Net Weight.");
      } else if (
        totalGrossWeightError === "Gross Weight must be >= Net Weight."
      ) {
        setTotalGrossWeightError("");
      }
    }
  };

  const generateRandomId = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 20; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const isExtraUnitsDraftReadOnlyForPartner =
    (inboundRequest?.extraUnitsIR ||
      inboundRequest?.extraUnitsIr ||
      inboundRequest?.ExtraUnitsIR) &&
    inboundRequest?.status === getInboundStatusType.draft &&
    (userGroup === groupTypes.supplier || userGroup === groupTypes.brand);

  const isExtraUnitsIR = inboundRequest?.ExtraUnitsIR;

  const disabledButton =
    isExtraUnitsDraftReadOnlyForPartner ||
    inboundRequest?.status === getInboundStatusType.approved ||
    inboundRequest?.status === getInboundStatusType.received ||
    inboundRequest?.status === getInboundStatusType.closed ||
    inboundRequest?.status === getInboundStatusType.partiallyReceived ||
    inboundRequest?.status === getInboundStatusType.inTransit ||
    (inboundRequest?.status !== getInboundStatusType.draft &&
      userGroup === groupTypes.supplier) ||
    (inboundRequest?.status !== getInboundStatusType.draft &&
      (inboundRequest?.status !== getInboundStatusType.pending ||
        inboundRequest?.purchase_order?.brand?.type === "Distribution") &&
      userGroup === groupTypes.brand);

  const isInvestedBrand =
    inboundRequest?.purchase_order?.brand?.type === "Invested";
  const showToleranceColumns =
    isInvestedBrand && userGroup !== groupTypes.supplier;

  const isBrandOrSupplierUser =
    userGroup === groupTypes.brand || userGroup === groupTypes.supplier;
  const isPartiallyReceived =
    inboundRequest?.status === getInboundStatusType.partiallyReceived;
  const isReadOnlyStatus =
    inboundRequest?.status === getInboundStatusType.closed ||
    isPartiallyReceived;
  const toBooleanChoice = (value) =>
    value === true || value === "true" || value === 1 || value === "1";
  const uploadCNForMissingItemsValue =
    inboundRequest?.uploadCNForMissingItems ??
    inboundRequest?.UploadCNForMissingItems;
  const shipMissingItemsValue =
    inboundRequest?.shipMissingItems ?? inboundRequest?.ShipMissingItems;
  const uploadCNForMissingItemsSelected = toBooleanChoice(
    uploadCNForMissingItemsValue,
  );
  const shipMissingItemsSelected = toBooleanChoice(shipMissingItemsValue);
  const missingItemsCreditNoteRawValue =
    inboundRequest?.creditNoteProvidedByBrand ??
    inboundRequest?.missingItemsCreditNoteDocument ??
    inboundRequest?.missingItemsCreditNote;
  const missingItemsCreditNoteStoredFileName =
    missingItemsCreditNoteRawValue?.split("/").pop() || "";
  const missingItemsCreditNoteAttachmentPath = missingItemsCreditNoteRawValue
    ? missingItemsCreditNoteRawValue.includes("/")
      ? missingItemsCreditNoteRawValue
      : `3PL Credit Note/${missingItemsCreditNoteRawValue}`
    : "";
  const shouldShowMissingItemsCreditNoteField =
    isPartiallyReceived && uploadCNForMissingItemsSelected;
  const shouldShowMissingItemsChoicesSummary =
    isPartiallyReceived &&
    (uploadCNForMissingItemsSelected || shipMissingItemsSelected);
  const shouldAskMissingItemsChoice =
    isBrandOrSupplierUser &&
    isPartiallyReceived &&
    !uploadCNForMissingItemsSelected &&
    !shipMissingItemsSelected;
  const missingItemsRowsForChoiceModal = selectedPOItems
    .map((poItem) => {
      const relatedPoItemQuantity = inboundRequest?.poItemsQuantities?.find(
        (item) => Number(item.po_item_id) === Number(poItem.internalId),
      );

      const expectedQtyRaw =
        quantityValues[poItem.id] ?? relatedPoItemQuantity?.qty ?? 0;
      const expectedQty = Number(expectedQtyRaw);

      const receivedQtyRaw = relatedPoItemQuantity?.received3PL;
      const hasReceivedQty =
        receivedQtyRaw !== null &&
        receivedQtyRaw !== undefined &&
        receivedQtyRaw !== "";
      const receivedQty = Number(receivedQtyRaw);

      if (
        !hasReceivedQty ||
        !Number.isFinite(expectedQty) ||
        !Number.isFinite(receivedQty) ||
        receivedQty >= expectedQty
      ) {
        return null;
      }

      return {
        sku: poItem.itemSku || "-",
        eanCode: poItem.eanCode || "-",
        unitCost: poItem.rate ?? null,
        colourDescription: poItem.colourDescription || "-",
        description: poItem.description || "-",
        expectedQty,
        receivedQty,
        missingUnits: expectedQty - receivedQty,
      };
    })
    .filter(Boolean);

  const roundUpToNextQuarterHour = (date) => {
    const roundedDate = new Date(date);
    const minutes = date.getMinutes();
    const remainder = minutes % 15;

    if (remainder !== 0) {
      roundedDate.setMinutes(minutes + (15 - remainder));
    }

    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    return new Date(roundedDate);
  };

  // Handle Open/Close Email History Modal
  const handleOpenEmailHistoryModal = () => {
    if (inboundRequestId) {
      dispatch(thunks.getInboundRequestEmailHistory(inboundRequestId));
    }
    setEmailHistoryModalVisible(true);
  };
  const handleCloseEmailHistoryModal = () => setEmailHistoryModalVisible(false);

  useEffect(() => {
    if (userGroup === groupTypes.supplier) {
      dispatch(thunks.getSupplierAddresses());
    } else if (userGroup === groupTypes.brand) {
      dispatch(thunks.getSupplierAddressesByPurchaseOrder());
    }
  }, [userGroup, user]);

  useEffect(() => {
    if (shouldAskMissingItemsChoice) {
      setMissingItemsChoiceModalVisible(true);
      setUploadCNForMissingItemsChoice(false);
      setShipMissingItemsChoice(false);
      setMissingItemsCreditNoteFileName("");
      setMissingItemsCreditNoteFileData(null);
    } else {
      setMissingItemsChoiceModalVisible(false);
    }
  }, [shouldAskMissingItemsChoice, inboundRequest?.id]);
  useEffect(() => {
    if (!inboundRequest || missingItemsCreditNoteFileData instanceof File) {
      return;
    }

    setMissingItemsCreditNoteFileName(missingItemsCreditNoteStoredFileName);
  }, [
    inboundRequest,
    missingItemsCreditNoteFileData,
    missingItemsCreditNoteStoredFileName,
  ]);

  const handleMissingItemsCreditNoteFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const allowedFiles = ["pdf", "xls", "xlsx", "doc", "docx"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!allowedFiles.includes(fileExtension)) {
      alert("Invalid file type! Please upload a PDF, Excel, or Word file.");
      event.target.value = "";
      return;
    }

    setMissingItemsCreditNoteFileName(file.name);
    setMissingItemsCreditNoteFileData(file);
  };

  const handleSaveMissingItemsCreditNote = async () => {
    if (
      !inboundRequest?.id ||
      !(missingItemsCreditNoteFileData instanceof File)
    ) {
      return;
    }

    setSaveMissingItemsCreditNoteLoading(true);
    try {
      await dispatch(
        thunks.saveCreditNote(
          inboundRequest.id,
          missingItemsCreditNoteFileData,
        ),
      );

      toast.success("Credit Note uploaded successfully");
      setMissingItemsCreditNoteFileData(null);
      dispatch(thunks.getInboundRequest(inboundRequest.id));
    } catch (error) {
      toast.error("Failed to upload Credit Note");
    } finally {
      setSaveMissingItemsCreditNoteLoading(false);
    }
  };
  const renderMissingItemsCreditNoteUploadField = (showSaveButton = false) => {
    const hasUnsavedFile = missingItemsCreditNoteFileData instanceof File;
    const fileToDisplay =
      missingItemsCreditNoteFileName || missingItemsCreditNoteStoredFileName;

    const truncatedName =
      fileToDisplay?.length > 50
        ? fileToDisplay.slice(0, 50) + "..."
        : fileToDisplay;

    return (
      <Stack style={{ gap: 10, marginBottom: 15 }}>
        <Stack
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <CustomButton
            component="label"
            label={
              <>
                {fileToDisplay ? "Replace Credit Note" : "Upload Credit Note"}
                <Input
                  type="file"
                  id="missing-items-credit-note-input"
                  accept=".pdf,.xls,.xlsx,.doc,.docx"
                  onChange={handleMissingItemsCreditNoteFileChange}
                  style={{ display: "none" }}
                />
              </>
            }
            style={{ width: "fit-content" }}
          />

          {fileToDisplay && (
            <Stack
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                fontFamily: "tmwCustomFont",
                fontSize: 12,
              }}
            >
              {hasUnsavedFile ? (
                <span>{fileToDisplay}</span>
              ) : (
                <Tooltip title="View file">
                  <span
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() =>
                      missingItemsCreditNoteAttachmentPath &&
                      handleViewAttachment(missingItemsCreditNoteAttachmentPath)
                    }
                  >
                    {truncatedName}
                  </span>
                </Tooltip>
              )}

              {hasUnsavedFile && (
                <ButtonBase
                  onClick={() => {
                    setMissingItemsCreditNoteFileData(null);
                    setMissingItemsCreditNoteFileName(
                      missingItemsCreditNoteStoredFileName,
                    );

                    const fileInput = document.getElementById(
                      "missing-items-credit-note-input",
                    );
                    if (fileInput) {
                      fileInput.value = "";
                    }
                  }}
                >
                  <CloseOutlined style={{ width: "18px" }} />
                </ButtonBase>
              )}
            </Stack>
          )}
        </Stack>

        {showSaveButton && hasUnsavedFile && (
          <Stack style={{ alignItems: "flex-start" }}>
            <CustomButton
              label="SAVE CREDIT NOTE"
              onClick={handleSaveMissingItemsCreditNote}
              disabled={saveMissingItemsCreditNoteLoading}
              loader={
                saveMissingItemsCreditNoteLoading && (
                  <CircularProgress
                    size={20}
                    color="inherit"
                    style={{ marginLeft: "10px" }}
                  />
                )
              }
              style={{ width: "fit-content" }}
            />
          </Stack>
        )}
      </Stack>
    );
  };

  const renderMissingItemsChoicesSummary = () => {
    return (
      <Stack
        style={{
          width: "400px",
          gap: 8,
          padding: "10px 12px",
          border: "1px solid #E6E6E6",
        }}
      >
        <CustomTypography
          message="Missing Units Choices"
          style={{ fontSize: 12, fontWeight: 600 }}
        />
        <Stack
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 10,
            fontFamily: "tmwCustomFont",
            fontSize: 12,
          }}
        >
          <span>Upload Credit Note:</span>
          <span>{uploadCNForMissingItemsSelected ? "YES" : "NO"}</span>
        </Stack>
        <Stack
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 10,
            fontFamily: "tmwCustomFont",
            fontSize: 12,
          }}
        >
          <Stack style={{ alignItems: "left" }}>
            <span>Ship Missing Units</span>
            <span>(New Inbound Request):</span>
          </Stack>
          <span>{shipMissingItemsSelected ? "YES" : "NO"}</span>
        </Stack>
      </Stack>
    );
  };

  // Autocomplete pickup fields
  useEffect(() => {
    if (supplierAddresses) {
      setPickupCompanyName(
        pickupCompanyName || supplierAddresses?.companyName || "",
      );

      const combinedAddress =
        (supplierAddresses?.addr1 || "") +
        (supplierAddresses?.addr2
          ? supplierAddresses?.addr1
            ? " " + supplierAddresses?.addr2
            : supplierAddresses?.addr2
          : "");
      setPickupAddress(pickupAddress || combinedAddress || "");

      setPickupZipCode(pickupZipCode || supplierAddresses?.zip || "");
      setPickupCity(pickupCity || supplierAddresses?.city || "");
      setPickupCountry(pickupCountry || supplierAddresses?.country || "");
    }
  }, [supplierAddresses]);

  // General filter PO Items
  useEffect(() => {
    const auxFilteredPOItems = POItems.filter((POItem) => {
      const search = searchPOItem.toLowerCase();
      return (
        POItem.description?.toLowerCase().includes(search) ||
        POItem.colourDescription?.toLowerCase().includes(search) ||
        POItem.itemSku?.toLowerCase().includes(search) ||
        POItem.internalId?.toLowerCase().includes(search) ||
        POItem.vendorCode?.toLowerCase().includes(search)
      );
    });

    setFilteredPOItems(auxFilteredPOItems);
  }, [POItems, searchPOItem]);

  //
  // FILE SELECTS
  //

  // Packing List
  const [packingListFileName, setPackingListFileName] = useState("");
  const [packingListFileData, setPackingListFileData] = useState(null);
  const [packingListFileError, setPackingListFileError] = useState("");
  // Invoice
  const [invoiceFileName, setInvoiceFileName] = useState("");
  const [invoiceFileData, setInvoiceFileData] = useState(null);
  const [invoiceFileError, setInvoiceFileError] = useState("");

  // Autocomplete
  const [autocompleteStates, setAutocompleteStates] = useState({});

  useEffect(() => {
    if (inboundRequestId) {
      dispatch(thunks.getInboundRequest(inboundRequestId));
    }
  }, [inboundRequestId]);

  useEffect(() => {
    if (inboundRequest) {
      dispatch(
        thunks.getInboundRequestsItemsQuantityOfPO(
          inboundRequest.purchase_order.id,
        ),
      );
    }
  }, [inboundRequest]);

  // Store original poItemsQuantities when first loaded
  useEffect(() => {
    if (
      inboundRequest?.poItemsQuantities &&
      inboundRequest.poItemsQuantities.length > 0 &&
      originalPoItemsQuantities.length === 0
    ) {
      setOriginalPoItemsQuantities([...inboundRequest.poItemsQuantities]);
    }
  }, [inboundRequest?.poItemsQuantities]);

  // Validate weight fields on initial load
  useEffect(() => {
    if (totalGrossWeight || totalNetWeight) {
      // Validate gross weight format and cross-validation
      if (totalGrossWeight) {
        const normalized = totalGrossWeight.toString().replace(",", ".");
        if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
          setTotalGrossWeightError(
            "Invalid number. Use '.' for decimals (e.g., 1234.56).",
          );
        } else if (totalNetWeight) {
          const netNormalized = totalNetWeight.toString().replace(",", ".");
          if (/^\d+(?:\.\d{1,2})?$/.test(netNormalized)) {
            const grossNum = parseFloat(normalized);
            const netNum = parseFloat(netNormalized);
            if (grossNum < netNum) {
              setTotalGrossWeightError("Gross Weight must be >= Net Weight.");
            } else {
              setTotalGrossWeightError("");
            }
          } else {
            setTotalGrossWeightError("");
          }
        } else {
          setTotalGrossWeightError("");
        }
      }

      // Validate net weight format and cross-validation
      if (totalNetWeight) {
        const normalized = totalNetWeight.toString().replace(",", ".");
        if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
          setTotalNetWeightError(
            "Invalid number. Use '.' for decimals (e.g., 1234.56).",
          );
        } else if (totalGrossWeight) {
          const grossNormalized = totalGrossWeight.toString().replace(",", ".");
          if (/^\d+(?:\.\d{1,2})?$/.test(grossNormalized)) {
            const netNum = parseFloat(normalized);
            const grossNum = parseFloat(grossNormalized);
            if (netNum > grossNum) {
              setTotalNetWeightError("Net Weight must be <= Gross Weight.");
            } else {
              setTotalNetWeightError("");
            }
          } else {
            setTotalNetWeightError("");
          }
        } else {
          setTotalNetWeightError("");
        }
      }
    }
  }, [totalGrossWeight, totalNetWeight]);

  useEffect(() => {
    if (
      firstLoad &&
      inboundRequest?.purchase_order &&
      inboundRequest?.purchase_order_items
    ) {
      setSelectedPOItems(inboundRequest.purchase_order_items);

      setPackingListFileName(inboundRequest.packingListDocument);
      setPackingListFileData(null);
      setInvoiceFileName(inboundRequest.invoice);
      setInvoiceFileData(null);
      setInvoiceNetTotal(Number(inboundRequest.invoiceNetTotal));
      setIncoterm(inboundRequest.incoterm);
      setTotalUnits(Number(inboundRequest.totalUnits));

      // Load pickup fields from inboundRequest
      setPickupConfirmationNumber(
        inboundRequest.pickupConfirmationNumber || "",
      );
      setTrackingNumber(inboundRequest.trackingNumber || "");
      setPickupDate(
        inboundRequest.pickupDate ? inboundRequest.pickupDate : new Date(),
      );
      setPickupTime(
        inboundRequest.pickupTime
          ? dayjs(inboundRequest.pickupTime, "HH:mm:ss")
          : roundUpToNextQuarterHour(new Date()),
      );
      setNumberOfBoxes(inboundRequest.numberOfBoxes || 0);
      setInvoiceNumber(inboundRequest.invoiceNumber || "");
      setCarrierName(inboundRequest.carrierName || "");
      setDeliveryAddress(inboundRequest.deliveryAddress || "");
      setDeliveryNumber(inboundRequest.deliveryNumber || "");
      setTotalNumberOfCartons(inboundRequest.totalNumberOfCartons || 0);
      setTotalNumberOfPallets(inboundRequest.totalNumberOfPallets || 0);
      setBoxDimensions(inboundRequest.boxDimensions || "");
      setPalletDimensions(inboundRequest.palletDimensions || "");
      setTotalGrossWeight(inboundRequest.totalGrossWeight || "");
      setTotalNetWeight(inboundRequest.totalNetWeight || "");
      setPickupCompanyName(inboundRequest.pickupCompanyName || "");
      setPickupZipCode(inboundRequest.pickupZipCode || "");
      setPickupCity(inboundRequest.pickupCity || "");
      setPickupCountry(inboundRequest.pickupCountry || "");
      setPickupAddress(inboundRequest.pickupAddress || "");
      setOpeningHours(inboundRequest.openingHours || "");
      setContactName(inboundRequest.contactName || "");
      setPhoneNumber(inboundRequest.phoneNumber || "");
      setSupplierEmailContact(inboundRequest.supplierEmailContact || "");
      setContentDetails(inboundRequest.contentDetails || "");
      setPackingListNumber(inboundRequest.packingListNumber || "");
      setComments(inboundRequest.comments || "");
      setReadyDateForPickup(inboundRequest.readyDateForPickup || "");

      dispatch(thunks.getPOItems(inboundRequest.purchase_order.internalId));
      setFirstLoad(false);
    }

    if (
      supplierAddresses &&
      inboundRequest?.purchase_order &&
      inboundRequest?.purchase_order_items
    ) {
      const combinedAddress =
        (supplierAddresses?.addr1 || "") +
        (supplierAddresses?.addr2
          ? supplierAddresses?.addr1
            ? " " + supplierAddresses?.addr2
            : supplierAddresses?.addr2
          : "");

      setPickupCompanyName(
        inboundRequest?.pickupCompanyName ||
          supplierAddresses?.companyName ||
          "",
      );
      setPickupZipCode(
        inboundRequest?.pickupZipCode || supplierAddresses?.zip || "",
      );
      setPickupCity(
        inboundRequest?.pickupCity || supplierAddresses?.city || "",
      );
      setPickupCountry(
        inboundRequest?.pickupCountry || supplierAddresses?.country || "",
      );
      setPickupAddress(inboundRequest?.pickupAddress || combinedAddress || "");
    }
  }, [firstLoad, inboundRequest, supplierAddresses]);

  useEffect(() => {
    // Validate incoterm presence whenever it or the request changes
    // Only validate for tomorrowCrew (not for Brand Contact or Supplier)
    if (userGroup !== groupTypes.tomorrowCrew) {
      setIncotermError("");
      return;
    }

    // Ensure inboundRequest is loaded before validating
    if (!inboundRequest || !inboundRequest?.purchase_order) {
      return;
    }

    // For TMW Crew: show Incoterm as required (red) when empty and status is Pending,
    // on both Invested and Distribution flows
    const isPending = inboundRequest?.status === getInboundStatusType.pending;
    const incotermValue = incoterm || "";
    const isEmpty = !incotermValue || incotermValue.trim() === "";

    if (isPending && isEmpty) {
      setIncotermError("Incoterm is required.");
    } else {
      setIncotermError("");
    }
  }, [incoterm, inboundRequest, userGroup]);

  useEffect(() => {
    const initialValues = {};
    const shortShipmentInitialValues = {};
    const extraUnitsReceivedInitialValues = {};

    filteredPOItems.forEach((poItem) => {
      const foundIRPOItem = inboundRequest.purchase_order_items.find(
        (poItem2) => poItem2.id === poItem.id,
      );
      const foundPOItem = inboundRequest.poItemsQuantities?.find(
        (poItem2) => poItem2.po_item_id === foundIRPOItem?.internalId,
      );

      if (foundPOItem) {
        initialValues[poItem.id] = foundPOItem?.qty;
        extraUnitsReceivedInitialValues[poItem.id] = 0;

        if (foundPOItem?.shortShipmentAnswer) {
          shortShipmentInitialValues[poItem.id] =
            foundPOItem?.shortShipmentAnswer;
        }
      }
    });

    setShortShipmentAnswers(shortShipmentInitialValues);
    setQuantityValues(initialValues);
    setExtraUnitsReceivedValues(extraUnitsReceivedInitialValues);
    setExtraUnitsReceivedChanged(false);
  }, [filteredPOItems]);

  useEffect(() => {
    if (!firstLoad) {
      setQuantityValues((prevValues) => {
        const newValues = { ...prevValues };

        selectedPOItems.forEach((POItem) => {
          if (newValues[POItem.id] === undefined) {
            newValues[POItem.id] = POItem.remainingQuantity;
          }
        });

        return newValues;
      });
    }
  }, [selectedPOItems, firstLoad]);

  useEffect(() => {
    if (!inboundRequest?.purchase_order_items) return;

    // Calculate based on selectedPOItems (current UI state) for validation
    const calculatedTotalUnits = selectedPOItems.reduce((sum, item) => {
      const qty = quantityValues[item.id];
      return sum + (qty && !isNaN(Number(qty)) ? Number(qty) : 0);
    }, 0);

    // Validate current totalUnits state against calculated value
    // This catches cases where items are added/removed but totalUnits wasn't updated
    if (totalUnits && !isNaN(Number(totalUnits))) {
      if (Number(totalUnits) !== calculatedTotalUnits) {
        setTotalUnitsError("Value must equal the Total Quantity.");
      } else {
        setTotalUnitsError("");
      }
    } else if (calculatedTotalUnits > 0) {
      // If there are items but totalUnits is empty, set error
      setTotalUnitsError("Value must equal the Total Quantity.");
    } else {
      setTotalUnitsError("");
    }
  }, [inboundRequest, quantityValues, selectedPOItems, totalUnits]);

  useEffect(() => {
    if (!inboundRequest?.purchase_order_items) return;

    // Calculate based on selectedPOItems (current UI state) for validation
    const calculatedNetTotal = selectedPOItems.reduce((sum, item) => {
      const qty = quantityValues[item.id];
      const rate = Number(item.rate) || 0;
      const itemQty = qty && !isNaN(Number(qty)) ? Number(qty) : 0;
      return sum + itemQty * rate;
    }, 0);

    // Validate current invoiceNetTotal state against calculated value
    // This catches cases where items are added/removed but invoiceNetTotal wasn't updated
    if (invoiceNetTotal && !isNaN(Number(invoiceNetTotal))) {
      const numericValue = Number(invoiceNetTotal);
      const calculatedNetTotalValue = Number(calculatedNetTotal);
      // Round to 2 decimal places to avoid floating-point precision issues
      const roundedNumericValue = Math.round(numericValue * 100) / 100;
      const roundedCalculatedValue =
        Math.round(calculatedNetTotalValue * 100) / 100;

      if (
        Number.isFinite(calculatedNetTotalValue) &&
        roundedNumericValue !== roundedCalculatedValue
      ) {
        setInvoiceNetTotalError(
          "Invoice Net Total must match Calculated Net Total.",
        );
      } else {
        setInvoiceNetTotalError("");
      }
    } else if (calculatedNetTotal > 0) {
      // If there's a calculated total but invoiceNetTotal is empty, set error
      setInvoiceNetTotalError("Invoice Net Total is required.");
    } else {
      setInvoiceNetTotalError("");
    }
  }, [inboundRequest, quantityValues, selectedPOItems, invoiceNetTotal]);

  useEffect(() => {
    const foundPOItems = [];
    inboundRequest?.poItemsQuantities.map((poItem) => {
      const foundIRPOItem = inboundRequest.purchase_order_items.find(
        (poItem2) => poItem2.internalId === poItem.po_item_id,
      );
      foundPOItems.push(foundIRPOItem);
    });

    // Skip this effect during initial load to prevent interfering with initial data loading
    if (firstLoad) {
      setFirstQuantityValues(foundPOItems);
      return;
    }

    if (selectAllItemsChecked) {
      const auxFilteredPOItems = filteredPOItems.filter((POItem) => {
        const isFoundInIR = foundPOItems.find(
          (POItem2) => POItem2.internalId === POItem.internalId,
        );

        // For Invested brands, only filter out cancelled items
        if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
          return !POItem.isCancelled;
        }
        // For non-Invested brands, keep original logic
        return (
          !POItem.isCancelled && (isFoundInIR || POItem.remainingQuantity > 0)
        );
      });
      const updatedFilteredPOItems = auxFilteredPOItems.map((POItem) => {
        const foundIR = inboundRequest.purchase_order_items.find(
          (POItem2) => POItem2.id === POItem.id,
        );
        if (foundIR) {
          return { ...POItem, remainingQuantity: foundIR.remainingQuantity };
        }
        return POItem;
      });
      setSelectedPOItems(updatedFilteredPOItems);
    } else {
      // When unchecked, clear all selected items
      setSelectedPOItems([]);
    }
  }, [selectAllItemsChecked, inboundRequest?.purchase_order?.brand?.type]);

  const invoiceNetTotalValidation = (value) => {
    const raw = "" + value ?? "";
    const normalized = raw.replace(",", ".");
    setInvoiceNetTotal(normalized);
    if (!normalized) {
      setInvoiceNetTotalError("Invoice Net Total is required.");
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      setInvoiceNetTotalError(
        "Invalid number. Use '.' for decimals (e.g., 1234.56).",
      );
    } else {
      // Calculate based on selectedPOItems (current UI state) for validation
      const calculatedNetTotal = selectedPOItems.reduce((sum, item) => {
        const qty = quantityValues[item.id];
        const rate = Number(item.rate) || 0;
        const itemQty = qty && !isNaN(Number(qty)) ? Number(qty) : 0;
        return sum + itemQty * rate;
      }, 0);

      // Compare against calculated net total from current items; flag if different
      // Round to 2 decimal places to avoid floating-point precision issues
      const numericValue = Number(normalized);
      const calculatedNetTotalValue = Number(calculatedNetTotal);
      const roundedNumericValue = Math.round(numericValue * 100) / 100;
      const roundedCalculatedValue =
        Math.round(calculatedNetTotalValue * 100) / 100;

      if (
        Number.isFinite(calculatedNetTotalValue) &&
        roundedNumericValue !== roundedCalculatedValue
      ) {
        setInvoiceNetTotalError(
          "Invoice Net Total must match Calculated Net Total.",
        );
      } else {
        setInvoiceNetTotalError("");
      }
    }
  };

  useEffect(() => {
    if (!inboundRequest || invoiceNetTotal === "") return;

    invoiceNetTotalValidation(invoiceNetTotal);
  }, [inboundRequest, inboundRequest?.calculatedNetTotal, invoiceNetTotal]);

  useEffect(() => {
    if (!inboundRequest) return;

    // Validate packing list file
    if (!packingListFileName && !packingListFileData) {
      setPackingListFileError("Packing List is required.");
    } else {
      setPackingListFileError("");
    }

    // Validate invoice file
    if (!invoiceFileName && !invoiceFileData) {
      setInvoiceFileError("Invoice is required.");
    } else {
      setInvoiceFileError("");
    }
  }, [
    inboundRequest,
    packingListFileName,
    packingListFileData,
    invoiceFileName,
    invoiceFileData,
  ]);

  useEffect(() => {
    if (!inboundRequest) return;

    dispatch(thunks.getPickupAutocompleteData(inboundRequestId));
  }, [inboundRequest]);

  // Populate approval modal fields
  useEffect(() => {
    if (inboundRequest) {
      // Parse inbshipRequestDate as local date to avoid timezone issues
      if (inboundRequest.inbshipRequestDate) {
        const parsedDate = dayjs(inboundRequest.inbshipRequestDate);
        if (parsedDate.isValid()) {
          // Create a Date object at local noon to avoid timezone/DST issues
          const localDate = new Date(
            parsedDate.year(),
            parsedDate.month(),
            parsedDate.date(),
            12,
            0,
            0,
            0,
          );
          setInbshipRequestDate(localDate);
        } else {
          setInbshipRequestDate(inboundRequest.inbshipRequestDate);
        }
      } else {
        // Autocomplete INBSHIP Request Date with today's date if not set
        setInbshipRequestDate(new Date());
      }

      // Parse expectedDeliveryDate as local date to avoid timezone issues
      // When date comes as string "YYYY-MM-DD", parse it as local date at noon
      if (inboundRequest.expectedDeliveryDate) {
        const parsedDate = dayjs(inboundRequest.expectedDeliveryDate);
        if (parsedDate.isValid()) {
          // Create a Date object at local noon to avoid timezone/DST issues
          const localDate = new Date(
            parsedDate.year(),
            parsedDate.month(),
            parsedDate.date(),
            12,
            0,
            0,
            0,
          );
          setExpectedDeliveryDate(localDate);
        } else {
          setExpectedDeliveryDate(inboundRequest.expectedDeliveryDate);
        }
      } else {
        setExpectedDeliveryDate(null);
      }

      setApprovalMemo(inboundRequest.approvalMemo);
    }
  }, [inboundRequest]);

  const handleEditInboundRequest = (shouldReload = false) => {
    const formattedSelectedPOItems = selectedPOItems.map((poItem) => {
      const existingExtraUnitsReceived = Number(
        poItem?.extraUnitsReceived ?? 0,
      );
      const currentExtraUnitsReceived = Number(
        extraUnitsReceivedValues[poItem.id] ?? existingExtraUnitsReceived,
      );

      const formatted = {
        ...poItem,
        strapiId: poItem.id,
        id: poItem.internalId,
        selectedQty: quantityValues[poItem.id],
        extraUnitsReceived: currentExtraUnitsReceived,
      };

      // Only include shortShipmentAnswer if it exists and quantity < remainingQuantity
      const currentQty = Number(quantityValues[poItem.id]);
      const remainingQty = Number(poItem.remainingQuantity);
      if (shortShipmentAnswers[poItem.id] && currentQty < remainingQty) {
        formatted.shortShipmentAnswer = shortShipmentAnswers[poItem.id];
      }

      return formatted;
    });

    const formData = new FormData();

    // Append fields
    formData.append("author", user.email);
    formData.append("purchaseOrderId", inboundRequest.purchase_order.id);
    formData.append("inboundRequestId", inboundRequest.id);
    formData.append(
      "documentNumber",
      inboundRequest.purchase_order.documentNumber,
    );

    // Append PO Items
    formData.append("poItems", JSON.stringify(formattedSelectedPOItems));

    // Append files
    if (packingListFileData instanceof File) {
      formData.append("packingList", packingListFileData);
    }

    if (invoiceFileData instanceof File) {
      formData.append("invoice", invoiceFileData);
    }

    formData.append("invoiceNetTotal", invoiceNetTotal);
    formData.append("incoterm", incoterm);
    formData.append("totalUnits", totalUnits);

    // Append Pickup Fields
    formData.append("pickupDate", pickupDate);
    formData.append("invoiceNumber", invoiceNumber);
    formData.append("deliveryAddress", deliveryAddress);
    formData.append("deliveryNumber", deliveryNumber);
    formData.append("totalNumberOfCartons", totalNumberOfCartons);
    formData.append("totalNumberOfPallets", totalNumberOfPallets);
    formData.append("boxDimensions", boxDimensions);
    formData.append("palletDimensions", palletDimensions);
    formData.append("totalGrossWeight", totalGrossWeight);
    formData.append("totalNetWeight", totalNetWeight);
    formData.append("pickupCompanyName", pickupCompanyName);
    formData.append("pickupZipCode", pickupZipCode);
    formData.append("pickupAddress", pickupAddress);
    formData.append("pickupCity", pickupCity);
    formData.append("pickupCountry", pickupCountry);
    formData.append("openingHours", openingHours);
    formData.append("contactName", contactName);
    formData.append("phoneNumber", phoneNumber);
    formData.append("supplierEmailContact", supplierEmailContact);
    formData.append("contentDetails", contentDetails);
    formData.append("packingListNumber", packingListNumber);
    formData.append("comments", comments);
    formData.append("readyDateForPickup", readyDateForPickup);

    return dispatch(thunks.editInboundRequest(formData)).then(() => {
      // Refresh the inbound request data after successful save
      return dispatch(thunks.getInboundRequest(inboundRequestId)).then(() => {
        setExtraUnitsReceivedChanged(false);
        // Reload the page only if explicitly requested (e.g., from Save Changes button)
        if (shouldReload) {
          window.location.reload();
        }
      });
    });
  };

  const hasAnyItemOutsideToleranceForInvested = () => {
    if (inboundRequest?.purchase_order?.brand?.type !== "Invested") {
      return false;
    }

    return selectedPOItems.some((poItem) => {
      const permittedDisplay = poItem.displayTolerance;
      const permittedNumeric = Number(
        poItem.acceptedToleranceAmount ?? permittedDisplay ?? 0,
      );

      const baseDiff = Number(poItem.difference ?? 0);
      const currentOld = Number(
        (originalPoItemsQuantities.length > 0
          ? originalPoItemsQuantities
          : inboundRequest?.poItemsQuantities || []
        ).find((x) => x.po_item_id === poItem.internalId)?.qty || 0,
      );
      const hasExplicitQty =
        Object.prototype.hasOwnProperty.call(quantityValues, poItem.id) &&
        quantityValues[poItem.id] !== "" &&
        !isNaN(Number(quantityValues[poItem.id]));
      const currentNew = hasExplicitQty
        ? Number(quantityValues[poItem.id])
        : currentOld;

      const adjustedDiff = baseDiff - currentOld + currentNew;
      const isOutsideTolerance =
        Math.abs(adjustedDiff) > Math.abs(permittedNumeric);

      const qtyLeft =
        firstQuantityValues.find((x) => x.id === poItem.id)
          ?.remainingQuantity ?? Number(poItem.remainingQuantity);
      const readyToShipQty = hasExplicitQty
        ? Number(quantityValues[poItem.id])
        : qtyLeft;

      const shortAnswer = shortShipmentAnswers[poItem.id];

      const isCase1 =
        readyToShipQty > qtyLeft &&
        isOutsideTolerance; /* Ready To Ship > Pending Qty AND outside tolerance */

      const isCase2 =
        readyToShipQty < qtyLeft &&
        shortAnswer === "Yes" &&
        isOutsideTolerance; /* Ready To Ship < Pending Qty AND Short_Shipment = Yes AND outside tolerance */

      return isCase1 || isCase2;
    });
  };

  const handleSubmitInboundRequestFromEdit = () => {
    // First save changes, then open the confirm modal
    handleEditInboundRequest()?.then(() => {
      setConfirmIRModalOpened(true);
    });
  };

  const handleSaveAndConfirmExtraUnits = () => {
    const extraUnitsItems = selectedPOItems.map((poItem) => ({
      poItemId: poItem.internalId,
      extraUnitsReceived: Number(extraUnitsReceivedValues[poItem.id] ?? 0),
    }));

    dispatch(
      thunks.confirmExtraUnitsReceived(inboundRequest.id, extraUnitsItems),
    );
    setExtraUnitsConfirmModalOpened(false);
    window.location.reload();
  };

  const handleOpenExtraUnitsModal = () => {
    const initialExtraUnitsValues = {};

    selectedPOItems.forEach((poItem) => {
      initialExtraUnitsValues[poItem.id] = 0;
    });

    setExtraUnitsReceivedValues(initialExtraUnitsValues);
    setExtraUnitsReceivedChanged(false);
    setExtraUnitsModalOpened(true);
  };

  const handleDraftInboundRequest = () => {
    setNotesModalAction("draft");
    setNotesModalVisible(true);
  };

  const handleSetToPendingInboundRequest = () => {
    setNotesModalAction("pending");
    setNotesModalVisible(true);
  };

  const handleConfirmMissingItemsChoice = async () => {
    const isDistributionBrand =
      inboundRequest?.purchase_order?.brand?.type === "Distribution";

    const finalUploadCNChoice =
      isDistributionBrand || uploadCNForMissingItemsChoice;
    const finalShipMissingItemsChoice = isDistributionBrand
      ? false
      : shipMissingItemsChoice;

    if (!finalUploadCNChoice && !finalShipMissingItemsChoice) {
      return;
    }

    if (!inboundRequest?.id) {
      return;
    }

    setConfirmMissingItemsChoiceLoading(true);
    try {
      await dispatch(
        thunks.confirmMissingItemsChoice(
          inboundRequest.id,
          finalUploadCNChoice,
          finalShipMissingItemsChoice,
          finalUploadCNChoice ? missingItemsCreditNoteFileData : null,
        ),
      );

      toast.success("Choices saved successfully");
      setMissingItemsChoiceModalVisible(false);
      setMissingItemsCreditNoteFileData(null);
      dispatch(thunks.getInboundRequest(inboundRequest.id));
    } catch (error) {
      toast.error("Failed to save choices");
    } finally {
      setConfirmMissingItemsChoiceLoading(false);
    }
  };

  const handleSubmitInboundRequest = () => {
    const isInvestedBrand =
      inboundRequest?.purchase_order?.brand?.type === "Invested";

    // For Invested brands: if all items are within tolerance, skip Pending and go directly to Authorized
    const skipPending =
      isInvestedBrand && !hasAnyItemOutsideToleranceForInvested();

    dispatch(thunks.submitInboundRequest(inboundRequest.id, skipPending));
    setConfirmIRModalOpened(false);
  };

  const handleCloseInboundRequest = async () => {
    if (!inboundRequest?.id) {
      return;
    }

    const isMarkAsReceivedAction =
      closeInboundRequestAction === "mark_received";

    setCloseInboundRequestLoading(true);
    try {
      if (isMarkAsReceivedAction) {
        await dispatch(thunks.markInboundRequestAsReceived(inboundRequest.id));
      } else {
        await dispatch(thunks.closeInboundRequest(inboundRequest.id));
      }
      toast.success(
        isMarkAsReceivedAction
          ? "The Inbound Request has been scheduled to be marked as Received."
          : "Inbound Request closed successfully",
      );
      setCloseIRModalOpened(false);
      dispatch(thunks.getInboundRequest(inboundRequest.id));
    } catch (error) {
      toast.error(
        (isMarkAsReceivedAction
          ? "Failed to mark Inbound Request as Received: "
          : "Failed to close Inbound Request: ") +
          error.response.data.error.message,
      );
    } finally {
      setCloseInboundRequestLoading(false);
    }
  };

  const handleNotifyChangeRequest = async () => {
    if (!inboundRequest?.id) {
      return;
    }

    if (!changeRequestNote?.trim()) {
      toast.error("Please add a change request note");
      return;
    }

    setNotifyChangeRequestLoading(true);
    try {
      await dispatch(
        thunks.notifyChangeRequest(inboundRequest.id, changeRequestNote.trim()),
      );
      toast.success("Change request notified successfully");
      setNotifyChangeRequestModalOpened(false);
      setChangeRequestNote("");
      dispatch(thunks.getInboundRequest(inboundRequest.id));
    } catch (error) {
      toast.error("Failed to notify change request");
    } finally {
      setNotifyChangeRequestLoading(false);
    }
  };

  const getItemsToModify = () => {
    if (inboundRequest?.purchase_order?.brand?.type !== "Invested") {
      return [];
    }

    return selectedPOItems
      .filter((poItem) => {
        const qtyLeft =
          firstQuantityValues.find((x) => x.id === poItem.id)
            ?.remainingQuantity ?? Number(poItem.remainingQuantity);

        const hasExplicitQty =
          Object.prototype.hasOwnProperty.call(quantityValues, poItem.id) &&
          quantityValues[poItem.id] !== "" &&
          !isNaN(Number(quantityValues[poItem.id]));

        const readyToShipQty = hasExplicitQty
          ? Number(quantityValues[poItem.id])
          : qtyLeft;

        const shortAnswer = shortShipmentAnswers[poItem.id];

        // Case 1: Ready To Ship > Pending Qty
        const isCase1 = readyToShipQty > qtyLeft;

        // Case 2: Ready To Ship < Pending Qty AND Short Shipment = Yes
        const isCase2 = readyToShipQty < qtyLeft && shortAnswer === "Yes";

        return isCase1 || isCase2;
      })
      .map((poItem) => {
        const qtyLeft =
          firstQuantityValues.find((x) => x.id === poItem.id)
            ?.remainingQuantity ?? Number(poItem.remainingQuantity);

        const hasExplicitQty =
          Object.prototype.hasOwnProperty.call(quantityValues, poItem.id) &&
          quantityValues[poItem.id] !== "" &&
          !isNaN(Number(quantityValues[poItem.id]));

        const readyToShipQty = hasExplicitQty
          ? Number(quantityValues[poItem.id])
          : qtyLeft;

        // To know if the quantity in NS will be increased or decreased
        const itemOperationType =
          readyToShipQty - qtyLeft > 0 ? "Increase" : "Decrease";

        return {
          internalId: poItem.internalId,
          lineId: poItem.lineId,
          currentQuantity: poItem.qty,
          quantityDifference: readyToShipQty - qtyLeft,
          itemOperationType,
        };
      });
  };

  const handleApproveInboundRequest = () => {
    const isInvestedBrand =
      inboundRequest?.purchase_order?.brand?.type === "Invested";

    if (
      isInvestedBrand &&
      inboundRequest?.status === getInboundStatusType.pending
    ) {
      handleEditInboundRequest().then(() => {
        dispatch(thunks.authorizeInboundRequest(inboundRequest.id));
      });
    } else {
      // Construim POItemsToUpdate cu toate itemele relevante din IR (qty > 0)
      const POItemsToUpdate = (inboundRequest?.purchase_order_items || [])
        .filter((item) => Number(item.qty) > 0)
        .map((item) => ({
          line: item.lineId,
          quantity: Number(item.qty),
        }));

      // Pentru istoric, doar cele modificate (păstrăm logica existentă)
      const itemsToModify = getItemsToModify();
      const POItemsHistory = [];
      if (itemsToModify && itemsToModify.length > 0) {
        itemsToModify.forEach((item) => {
          const historyItemObject = {
            amended: true,
            currentQuantity: item.currentQuantity + item.quantityDifference,
            initialQuantity: item.currentQuantity,
            purchaseOrderItemInternalId: item.internalId,
          };
          POItemsHistory.push(historyItemObject);
        });
      }

      // Format dates as YYYY-MM-DD strings to avoid timezone/DST issues
      const formattedInbshipRequestDate = inbshipRequestDate
        ? dayjs(inbshipRequestDate).format("YYYY-MM-DD")
        : inbshipRequestDate;
      const formattedExpectedDeliveryDate = expectedDeliveryDate
        ? dayjs(expectedDeliveryDate).format("YYYY-MM-DD")
        : expectedDeliveryDate;

      dispatch(
        thunks.approveInboundRequest(
          inboundRequest.id,
          incoterm,
          formattedInbshipRequestDate,
          formattedExpectedDeliveryDate,
          approvalMemo,
          POItemsToUpdate,
          POItemsHistory,
        ),
      );
    }
  };

  const renderBackButton = () => {
    return (
      <Stack
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "5px 25px",
        }}
      >
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            cursor: "pointer",
          }}
          sx={{
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() =>
            (window.location.href = window.location.href.includes("requests")
              ? paths.dashboard.inbound.requests.index
              : paths.dashboard.inbound.asn.index)
          }
        >
          <KeyboardBackspace />
          <CustomTypography message="Inbound Requests" />
        </Stack>
      </Stack>
    );
  };

  const renderPOInfo = () => {
    if (!inboundRequest?.purchase_order) return null;

    const align = mdUp ? "horizontal" : "vertical";

    const createdDate = inboundRequest.purchase_order.nsCreatedDate
      ? moment(inboundRequest.purchase_order.nsCreatedDate).format("DD/MM/YYYY")
      : "No Date";
    const openDeliveryWindow = inboundRequest.purchase_order.openDeliveryWindow
      ? moment(inboundRequest.purchase_order.openDeliveryWindow).format(
          "DD/MM/YYYY",
        )
      : "No Date";
    const closeDeliveryWindow = inboundRequest.purchase_order
      .closeDeliveryWindow
      ? moment(inboundRequest.purchase_order.closeDeliveryWindow).format(
          "DD/MM/YYYY",
        )
      : "No Date";

    const statusCircleColor =
      inboundRequest.status === getInboundStatusType.draft
        ? "#F5911E"
        : inboundRequest.status === getInboundStatusType.pending
          ? "#FF0000"
          : inboundRequest.status === getInboundStatusType.authorized
            ? "#882288"
            : inboundRequest.status === getInboundStatusType.approved
              ? "#39B349"
              : inboundRequest.status === getInboundStatusType.inTransit
                ? "#d0d000"
                : inboundRequest.status === getInboundStatusType.received
                  ? "#55aaff"
                  : inboundRequest.status ===
                      getInboundStatusType.partiallyReceived
                    ? "#55eecc"
                    : inboundRequest.status === getInboundStatusType.closed
                      ? "black"
                      : "grey";

    const ExpandMore = styled((props) => {
      const { expand, ...other } = props;
      return <IconButton {...other} />;
    })(({ theme, expand }) => ({
      transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
      marginLeft: "auto",
      marginBottom: "auto",
      transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
      }),
    }));

    const PropertyListColumn = ({ label, value, align }) => (
      <div style={{ flex: "1", textAlign: align }}>
        <PropertyList>
          <PropertyListItem label={label} value={value} />
        </PropertyList>
      </div>
    );

    return (
      <Card style={{ boxShadow: "none", marginBottom: 10 }}>
        <Stack
          style={{
            marginLeft: "25px",
            display: "flex",
            flexDirection: "row",
            gap: "15px",
            justifyContent: "space-between",
            padding: "12px 25px",
          }}
        >
          <Stack style={{ flexDirection: "row", gap: "15px" }}>
            <Stack style={{ justifyContent: "center" }}>
              <div
                style={{
                  margin: "5px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  border: `1.5px solid ${statusCircleColor}`,
                  background: statusCircleColor,
                }}
              />
            </Stack>
            <Stack
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
              }}
            >
              <Stack
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 2,
                  fontSize: 23,
                  fontWeight: 500,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                {inboundRequest.purchase_order.documentNumber}
                {(inboundRequest.status === getInboundStatusType.approved ||
                  inboundRequest.status === getInboundStatusType.inTransit ||
                  inboundRequest.status === getInboundStatusType.received ||
                  inboundRequest.status ===
                    getInboundStatusType.partiallyReceived ||
                  inboundRequest.status === getInboundStatusType.closed) &&
                  inboundRequest.inboundShippingNumber && (
                    <span
                      style={{
                        fontFamily: "tmwCustomFont",
                        marginLeft: 9,
                        fontSize: 20,
                        fontWeight: 600,
                      }}
                    >
                      {` | ASN: ${inboundRequest.inboundShippingNumber}`}
                    </span>
                  )}
              </Stack>
              <Stack
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {`Inbound Request Status: ${inboundRequest.status}`}
                {` | Brand: ${
                  inboundRequest.purchase_order.brandName ||
                  inboundRequest.purchase_order.brand?.name ||
                  "N/A"
                }`}
                {` | Season: ${inboundRequest.purchase_order.season || "N/A"}`}
                {` | Incoterm: ${inboundRequest.incoterm || "N/A"}`}
              </Stack>
              <Stack
                style={{
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {`NS Transaction Status: ${inboundRequest.purchase_order.nsTransactionStatus}`}
              </Stack>
            </Stack>
          </Stack>
          <div>
            <ExpandMore
              expand={poSummaryExpanded}
              onClick={() => setPoSummaryExpanded(!poSummaryExpanded)}
              aria-expanded={poSummaryExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </div>
        </Stack>
        <Collapse in={poSummaryExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Created Date:"
                value={createdDate}
                align={align}
              />
              <PropertyListColumn
                label="Document Number:"
                value={inboundRequest.purchase_order.documentNumber}
                align={align}
              />
              <PropertyListColumn
                label="Supplier:"
                value={inboundRequest.purchase_order.supplier}
                align={align}
              />
              <PropertyListColumn
                label="Memo:"
                value={inboundRequest.purchase_order.memo}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Subsidiary:"
                value={inboundRequest.purchase_order.subsidiary}
                align={align}
              />
              <PropertyListColumn
                label="Location:"
                value={inboundRequest.purchase_order.location}
                align={align}
              />
              <PropertyListColumn
                label="Brand:"
                value={
                  inboundRequest.purchase_order.brandName ||
                  inboundRequest.purchase_order.brand?.name
                }
                align={align}
              />
              <PropertyListColumn
                label="Season:"
                value={inboundRequest.purchase_order.season}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Currency:"
                value={inboundRequest.purchase_order.currency}
                align={align}
              />
              <PropertyListColumn
                label="Total Amount:"
                value={inboundRequest.purchase_order.amountForeignCurrency?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Total Open Lines:"
                value={inboundRequest.purchase_order.totalOpenLines?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Total Closed Lines:"
                value={inboundRequest.purchase_order.totalClosedLines?.toLocaleString?.()}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Total Open Quantity:"
                value={inboundRequest.purchase_order.totalOpenQty?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Total Closed Quantity:"
                value={inboundRequest.purchase_order.totalClosedQty?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Quantity Received:"
                value={inboundRequest.purchase_order.qtyReceived?.toLocaleString?.()}
                align={align}
              />
              <PropertyListColumn
                label="Quantity Billed:"
                value={inboundRequest.purchase_order.qtyBilled?.toLocaleString?.()}
                align={align}
              />
            </div>
            <Divider />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <PropertyListColumn
                label="Incoterm:"
                value={inboundRequest.purchase_order.incoterm}
                align={align}
              />
              <PropertyListColumn
                label="Open Delivery Window:"
                value={openDeliveryWindow}
                align={align}
              />
              <PropertyListColumn
                label="Close Delivery Window:"
                value={closeDeliveryWindow}
                align={align}
              />
              <PropertyListColumn
                label="Netsuite Transaction Status:"
                value={inboundRequest.purchase_order.nsTransactionStatus}
                align={align}
              />
            </div>
            <Divider />
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  const renderInvoiceNumber = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Invoice number:"
          style={{ fontSize: 12, width: "200px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={invoiceNumber || ""}
          handleChange={(e) => {
            setInvoiceNumber(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              e.target.value !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          error={!disabledButton && !invoiceNumber}
          helperText={
            !disabledButton && !invoiceNumber && "Invoice number is required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderCarrierName = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTextField
          label="Carrier Name"
          style={{ width: "100%" }}
          value={carrierName || ""}
          handleChange={(e) => setCarrierName(e.target.value)}
        />
      </Stack>
    );
  };

  const renderDeliveryAddress = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Delivery Address (Tomorrow warehouse location):"
          style={{ fontSize: 12, width: "400px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={deliveryAddress || ""}
          handleChange={(e) => {
            setDeliveryAddress(e.target.value);
            const hasChanges =
              e.target.value !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderDeliveryNumber = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    return (
      <Stack
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <CustomTypography
          message="Delivery number:"
          style={{
            fontSize: 12,
            width: "220px",
          }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={deliveryNumber || ""}
          handleChange={(e) => {
            setDeliveryNumber(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              e.target.value !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderTotalNumberOfCartons = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    return (
      <Stack
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 25,
        }}
      >
        <CustomTypography
          message="Total Number of Cartons:"
          style={{ fontSize: 12 }}
        />
        <CustomTextField
          style={{ width: "250px" }}
          value={totalNumberOfCartons}
          type="number"
          handleChange={(e) => {
            setTotalNumberOfCartons(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              e.target.value !== (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (totalNumberOfCartons === null ||
              totalNumberOfCartons === undefined ||
              totalNumberOfCartons === "" ||
              isNaN(Number(totalNumberOfCartons)) ||
              Number(totalNumberOfCartons) < 0)
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (totalNumberOfCartons === null ||
              totalNumberOfCartons === undefined ||
              totalNumberOfCartons === "" ||
              isNaN(Number(totalNumberOfCartons)) ||
              Number(totalNumberOfCartons) < 0) &&
            "Total number of cartons is required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderTotalNumberOfPallets = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 25 }}>
        <CustomTypography
          message="Total Number of Pallets:"
          style={{ fontSize: 12 }}
        />
        <CustomTextField
          style={{ width: "250px" }}
          value={totalNumberOfPallets}
          type="number"
          handleChange={(e) => {
            setTotalNumberOfPallets(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              e.target.value !== (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (totalNumberOfPallets === null ||
              totalNumberOfPallets === undefined ||
              totalNumberOfPallets === "" ||
              isNaN(Number(totalNumberOfPallets)) ||
              Number(totalNumberOfPallets) < 0)
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (totalNumberOfPallets === null ||
              totalNumberOfPallets === undefined ||
              totalNumberOfPallets === "" ||
              isNaN(Number(totalNumberOfPallets)) ||
              Number(totalNumberOfPallets) < 0) &&
            "Total number of pallets is required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderBoxDimensions = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginTop: "10px",
        }}
      >
        <CustomTypography
          message="Box Dimensions (CM/INCH):"
          style={{ fontSize: 12, width: "400px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={boxDimensions || ""}
          handleChange={(e) => {
            setBoxDimensions(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              e.target.value !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          multiline
          rows={2}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !boxDimensions &&
            !isSupplierDisabled
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !boxDimensions &&
            !isSupplierDisabled &&
            "Box dimensions are required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderPalletDimensions = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="If pallet, pallet dimensions (CM/INCH):"
          style={{ fontSize: 12, width: "400px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={palletDimensions || ""}
          handleChange={(e) => {
            setPalletDimensions(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              e.target.value !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          multiline
          rows={2}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !palletDimensions &&
            !isSupplierDisabled
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !palletDimensions &&
            !isSupplierDisabled &&
            "Pallet dimensions are required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderTotalGrossWeight = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Total Gross Weight (KG/LB):"
          style={{ fontSize: 12, width: "230px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={totalGrossWeight || ""}
          type="text"
          inputProps={{ inputMode: "decimal" }}
          handleChange={(e) => {
            totalGrossWeightValidation(e.target.value);
            const normalizedValue = e.target.value.replace(",", ".");
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              normalizedValue !== (inboundRequest.totalGrossWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (!!totalGrossWeightError ||
              (!totalGrossWeight && !isSupplierDisabled))
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (totalGrossWeightError ||
              (!totalGrossWeight &&
                !isSupplierDisabled &&
                "Total gross weight is required."))
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderTotalNetWeight = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Total Net Weight (KG/LB):"
          style={{ fontSize: 12, width: "230px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={totalNetWeight || ""}
          type="text"
          inputProps={{ inputMode: "decimal" }}
          handleChange={(e) => {
            totalNetWeightValidation(e.target.value);
            const normalizedValue = e.target.value.replace(",", ".");
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              normalizedValue !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (!!totalNetWeightError || (!totalNetWeight && !isSupplierDisabled))
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            (totalNetWeightError ||
              (!totalNetWeight &&
                !isSupplierDisabled &&
                "Total net weight is required."))
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderPickupAutocompleteField = (
    label,
    value,
    setValue,
    suggestions,
    disabled = false,
    fieldName = "",
  ) => {
    const fieldId = fieldName || label.toLowerCase().replace(/[^a-z0-9]/g, "");

    const fieldState = autocompleteStates[fieldId] || {
      showSuggestions: false,
      filteredSuggestions: [],
    };

    const updateFieldState = (updates) => {
      setAutocompleteStates((prev) => ({
        ...prev,
        [fieldId]: { ...fieldState, ...updates },
      }));
    };

    const handleInputChange = (e) => {
      const inputValue = e.target.value;
      setValue(inputValue);

      if (inputValue && suggestions && suggestions.length > 0) {
        const filtered = suggestions.filter((suggestion) =>
          suggestion.toLowerCase().includes(inputValue.toLowerCase()),
        );
        updateFieldState({
          filteredSuggestions: filtered,
          showSuggestions: filtered.length > 0,
        });
      } else if (suggestions && suggestions.length > 0) {
        // Show all suggestions when input is empty
        updateFieldState({
          filteredSuggestions: suggestions,
          showSuggestions: true,
        });
      } else {
        updateFieldState({ showSuggestions: false });
      }

      // Update pickup details changed state
      const hasChanges =
        deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
        pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
        pickupAddress !== (inboundRequest.pickupAddress || "") ||
        pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
        pickupCity !== (inboundRequest.pickupCity || "") ||
        pickupCountry !== (inboundRequest.pickupCountry || "") ||
        openingHours !== (inboundRequest.openingHours || "") ||
        contactName !== (inboundRequest.contactName || "") ||
        phoneNumber !== (inboundRequest.phoneNumber || "") ||
        supplierEmailContact !== (inboundRequest.supplierEmailContact || "") ||
        contentDetails !== (inboundRequest.contentDetails || "") ||
        comments !== (inboundRequest.comments || "") ||
        deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
        packingListNumber !== (inboundRequest.packingListNumber || "") ||
        invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
        totalNumberOfCartons !== (inboundRequest.totalNumberOfCartons || 0) ||
        totalNumberOfPallets !== (inboundRequest.totalNumberOfPallets || 0) ||
        boxDimensions !== (inboundRequest.boxDimensions || "") ||
        palletDimensions !== (inboundRequest.palletDimensions || "") ||
        totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
        totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
        pickupDate !== (inboundRequest.pickupDate || new Date());
      setPickupDetailsChanged(hasChanges);
    };

    const handleSuggestionClick = (suggestion) => {
      setValue(suggestion);

      const hasChanges =
        fieldName === "pickupCompanyName"
          ? suggestion !== (inboundRequest.pickupCompanyName || "")
          : fieldName === "pickupAddress"
            ? suggestion !== (inboundRequest.pickupAddress || "")
            : fieldName === "pickupZipCode"
              ? suggestion !== (inboundRequest.pickupZipCode || "")
              : fieldName === "pickupCity"
                ? suggestion !== (inboundRequest.pickupCity || "")
                : fieldName === "pickupCountry"
                  ? suggestion !== (inboundRequest.pickupCountry || "")
                  : fieldName === "openingHours"
                    ? suggestion !== (inboundRequest.openingHours || "")
                    : fieldName === "contactName"
                      ? suggestion !== (inboundRequest.contactName || "")
                      : fieldName === "phoneNumber"
                        ? suggestion !== (inboundRequest.phoneNumber || "")
                        : fieldName === "supplierEmailContact"
                          ? suggestion !==
                            (inboundRequest.supplierEmailContact || "")
                          : false;

      setPickupDetailsChanged(hasChanges);

      updateFieldState({ showSuggestions: false });
    };

    const handleInputClick = (e) => {
      if (suggestions && suggestions.length > 0) {
        updateFieldState({
          filteredSuggestions: suggestions,
          showSuggestions: true,
        });
      }
    };

    const handleInputBlur = () => {
      // Delay hiding suggestions to allow for clicks
      setTimeout(() => updateFieldState({ showSuggestions: false }), 200);
    };

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    const pickupLocationFieldNames = [
      "pickupCompanyName",
      "pickupAddress",
      "pickupZipCode",
      "pickupCity",
      "pickupCountry",
    ];

    const isPickupLocationField = pickupLocationFieldNames.includes(fieldName);
    const isFieldRequired = isPickupLocationField || !isDDPIncoterm;

    return (
      <Stack
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          position: "relative",
        }}
      >
        <CustomTypography
          message={label}
          style={{ fontSize: 12, width: "210px" }}
        />
        <Stack style={{ width: "100%", position: "relative" }}>
          <div onClick={handleInputClick}>
            <CustomTextField
              style={{ width: "100%" }}
              value={value || ""}
              handleChange={handleInputChange}
              onBlur={handleInputBlur}
              error={
                !isExtraUnitsDraftReadOnlyForPartner &&
                !value &&
                !isSupplierDisabled &&
                isFieldRequired
              }
              helperText={
                !isExtraUnitsDraftReadOnlyForPartner &&
                !value &&
                !isSupplierDisabled &&
                isFieldRequired &&
                "This field is required."
              }
              disabled={disabled}
            />
          </div>
          {fieldState.showSuggestions &&
            fieldState.filteredSuggestions.length > 0 && (
              <Paper
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #E6E6E6",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <List style={{ padding: 0 }}>
                  {fieldState.filteredSuggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        padding: "8px 16px",
                        fontSize: "12px",
                        fontFamily: "tmwCustomFont",
                      }}
                    >
                      <ListItemText primary={suggestion} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
        </Stack>
      </Stack>
    );
  };

  const renderPickupCompanyName = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupCompanyNames || [];

    return renderPickupAutocompleteField(
      "Pickup Company Name:",
      pickupCompanyName,
      setPickupCompanyName,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "pickupCompanyName",
    );
  };

  const renderPickupAddress = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupAddresses || [];

    return renderPickupAutocompleteField(
      "Pickup Address:",
      pickupAddress,
      setPickupAddress,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "pickupAddress",
    );
  };

  const renderPickupZipCode = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupZipCodes || [];

    return renderPickupAutocompleteField(
      "Pickup Zip Code:",
      pickupZipCode,
      setPickupZipCode,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "pickupZipCode",
    );
  };

  const renderPickupCity = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions = pickupAutocompleteData?.suggestions?.pickupCities || [];

    return renderPickupAutocompleteField(
      "Pickup City:",
      pickupCity,
      setPickupCity,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "pickupCity",
    );
  };

  const renderPickupCountry = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions =
      pickupAutocompleteData?.suggestions?.pickupCountries || [];

    return renderPickupAutocompleteField(
      "Pickup Country:",
      pickupCountry,
      setPickupCountry,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "pickupCountry",
    );
  };

  const renderOpeningHours = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions = pickupAutocompleteData?.suggestions?.openingHours || [];

    return renderPickupAutocompleteField(
      "Opening Hours:",
      openingHours,
      setOpeningHours,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "openingHours",
    );
  };

  const renderContactName = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions = pickupAutocompleteData?.suggestions?.contactNames || [];

    return renderPickupAutocompleteField(
      "Contact Name:",
      contactName,
      setContactName,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "contactName",
    );
  };

  const renderPhoneNumber = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions = pickupAutocompleteData?.suggestions?.phoneNumbers || [];

    return renderPickupAutocompleteField(
      "Phone Number:",
      phoneNumber,
      setPhoneNumber,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "phoneNumber",
    );
  };

  const renderSupplierEmailContact = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const suggestions =
      pickupAutocompleteData?.suggestions?.supplierEmailContacts || [];

    return renderPickupAutocompleteField(
      "Email:",
      supplierEmailContact,
      setSupplierEmailContact,
      suggestions,
      disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized),
      "supplierEmailContact",
    );
  };

  const renderContentDetails = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Content Details (Product Category):"
          style={{ fontSize: 12, width: "250px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={contentDetails || ""}
          placeholder="Example: clothing, footwear…"
          handleChange={(e) => {
            setContentDetails(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              e.target.value !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          multiline
          rows={2}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !contentDetails &&
            !isSupplierDisabled
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !contentDetails &&
            !isSupplierDisabled &&
            "Content details are required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderPackingListNumber = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Packing List Number:"
          style={{ fontSize: 12, width: "250px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={packingListNumber || ""}
          handleChange={(e) => {
            setPackingListNumber(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              e.target.value !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          error={!disabledButton && !packingListNumber}
          helperText={
            !disabledButton &&
            !packingListNumber &&
            "Packing list number is required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderComments = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Comments:"
          style={{ fontSize: 12, width: "130px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={comments || ""}
          handleChange={(e) => {
            setComments(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              e.target.value !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date());
            setPickupDetailsChanged(hasChanges);
          }}
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderReadyDateForPickup = () => {
    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const isSupplierDisabled =
      userGroup === groupTypes.supplier &&
      inboundRequest.status !== getInboundStatusType.draft;

    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTypography
          message="Ready Date for Pick-up:"
          style={{ fontSize: 12, width: "320px" }}
        />
        <CustomTextField
          style={{ width: "100%" }}
          value={readyDateForPickup || ""}
          handleChange={(e) => {
            setReadyDateForPickup(e.target.value);
            const hasChanges =
              deliveryAddress !== (inboundRequest.deliveryAddress || "") ||
              pickupCompanyName !== (inboundRequest.pickupCompanyName || "") ||
              pickupAddress !== (inboundRequest.pickupAddress || "") ||
              pickupZipCode !== (inboundRequest.pickupZipCode || "") ||
              pickupCity !== (inboundRequest.pickupCity || "") ||
              pickupCountry !== (inboundRequest.pickupCountry || "") ||
              openingHours !== (inboundRequest.openingHours || "") ||
              contactName !== (inboundRequest.contactName || "") ||
              phoneNumber !== (inboundRequest.phoneNumber || "") ||
              supplierEmailContact !==
                (inboundRequest.supplierEmailContact || "") ||
              contentDetails !== (inboundRequest.contentDetails || "") ||
              comments !== (inboundRequest.comments || "") ||
              deliveryNumber !== (inboundRequest.deliveryNumber || "") ||
              packingListNumber !== (inboundRequest.packingListNumber || "") ||
              invoiceNumber !== (inboundRequest.invoiceNumber || "") ||
              totalNumberOfCartons !==
                (inboundRequest.totalNumberOfCartons || 0) ||
              totalNumberOfPallets !==
                (inboundRequest.totalNumberOfPallets || 0) ||
              boxDimensions !== (inboundRequest.boxDimensions || "") ||
              palletDimensions !== (inboundRequest.palletDimensions || "") ||
              totalGrossWeight !== (inboundRequest.totalGrossWeight || "") ||
              totalNetWeight !== (inboundRequest.totalNetWeight || "") ||
              pickupDate !== (inboundRequest.pickupDate || new Date()) ||
              e.target.value !== (inboundRequest.readyDateForPickup || "");
            setPickupDetailsChanged(hasChanges);
          }}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !readyDateForPickup &&
            !isSupplierDisabled &&
            !isDDPIncoterm
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !readyDateForPickup &&
            !isSupplierDisabled &&
            !isDDPIncoterm &&
            "Ready date for pickup is required."
          }
          disabled={
            disabledButton || (isAuthorizedTmwCrew && !isEditingAuthorized)
          }
        />
      </Stack>
    );
  };

  const renderPickUpConfirmationNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTextField
          label="Pick-up Confirmation Number"
          style={{ width: "100%" }}
          value={pickupConfirmationNumber || ""}
          handleChange={(e) => setPickupConfirmationNumber(e.target.value)}
        />
      </Stack>
    );
  };

  const renderTrackingNumber = () => {
    return (
      <Stack style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <CustomTextField
          label="Tracking Number"
          style={{ width: "100%" }}
          value={trackingNumber || ""}
          handleChange={(e) => setTrackingNumber(e.target.value)}
        />
      </Stack>
    );
  };

  const renderPickupDetailsSection = () => {
    const renderDivider = (
      <div
        style={{
          width: "100%",
          border: "1px dashed #E6E6E6",
        }}
      />
    );

    return (
      <Stack
        style={{
          width: "95%",
          alignItems: "flex-start",
        }}
      >
        <Stack
          sx={{
            minWidth: "200px",
            width: { xs: "95%", md: "80%" },
            padding: "25px",
            border: "1px solid #E6E6E6",
            margin: "25px 0",
            position: "relative",
          }}
        >
          <CustomTypography
            message="Shipping and delivery information"
            title
            style={{ marginBottom: "25px" }}
          />
          <Stack style={{ width: "100%", gap: 24 }}>
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Shipment details"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>
                {renderDeliveryNumber()}
                {renderPackingListNumber()}
                {renderInvoiceNumber()}
                {renderTotalNumberOfCartons()}
                {renderTotalNumberOfPallets()}
                {renderBoxDimensions()}
                {renderPalletDimensions()}
                {renderTotalGrossWeight()}
                {renderTotalNetWeight()}
                {renderContentDetails()}
                {renderComments()}
              </Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Pickup location"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>{renderPickupCompanyName()}</Stack>
              <Stack>{renderPickupAddress()}</Stack>
              <Stack>{renderPickupZipCode()}</Stack>
              <Stack>{renderPickupCity()}</Stack>
              <Stack>{renderPickupCountry()}</Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Delivery address"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>{renderDeliveryAddress()}</Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Schedule and Availability"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>
                {renderOpeningHours()}
                {renderReadyDateForPickup()}
              </Stack>
            </Stack>
            {renderDivider}
            <Stack style={{ gap: 24 }}>
              <Stack style={{ width: "100%", alignItems: "flex-start" }}>
                <CustomTypography
                  title
                  message="Contact Information"
                  style={{ fontSize: 14 }}
                />
              </Stack>
              <Stack>
                {renderContactName()}
                {renderPhoneNumber()}
                {renderSupplierEmailContact()}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const renderPOItems = () => {
    const showReceived3PLColumn =
      (userGroup === groupTypes.tomorrowCrew ||
        userGroup === groupTypes.brand ||
        userGroup === groupTypes.supplier) &&
      (inboundRequest?.status === getInboundStatusType.partiallyReceived ||
        inboundRequest?.status === getInboundStatusType.received ||
        inboundRequest?.status === getInboundStatusType.closed);

    const getBackendReceived3PL = (poItem) => {
      const foundPOItem = inboundRequest?.poItemsQuantities?.find(
        (item) => item.po_item_id === poItem.internalId,
      );

      if (
        foundPOItem?.received3PL === null ||
        foundPOItem?.received3PL === undefined
      ) {
        return "-";
      }

      return Number(foundPOItem.received3PL);
    };

    const getBackendExtraUnitsValue = (poItem) => {
      const foundPOItem = inboundRequest?.poItemsQuantities?.find(
        (item) => item.po_item_id === poItem.internalId,
      );

      const rawValue = foundPOItem?.extraUnitsReceived;

      if (rawValue === null || rawValue === undefined || rawValue === "") {
        return null;
      }

      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const showExtraUnitsColumn = selectedPOItems.some((poItem) => {
      const value = getBackendExtraUnitsValue(poItem);
      return value !== null && value > 0;
    });

    return (
      <Stack
        style={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <Stack
          style={{
            position: "relative",
            width: "95%",
            padding: "25px",
            border:
              inboundRequest.status === getInboundStatusType.approved
                ? "1px solid green"
                : inboundRequest.status === getInboundStatusType.inTransit
                  ? "1px solid #d0d000"
                  : inboundRequest.status === getInboundStatusType.received
                    ? "1px solid #55aaff"
                    : inboundRequest.status ===
                        getInboundStatusType.partiallyReceived
                      ? "1px solid #55eecc"
                      : inboundRequest.status === getInboundStatusType.draft
                        ? "1px solid orange"
                        : inboundRequest.status === getInboundStatusType.pending
                          ? "1px solid red"
                          : inboundRequest.status ===
                              getInboundStatusType.authorized
                            ? "1px solid #882288"
                            : inboundRequest.status ===
                                getInboundStatusType.closed
                              ? "1px solid black"
                              : "none",
          }}
        >
          {!isExtraUnitsIR &&
            inboundRequest.status !== getInboundStatusType.approved &&
            inboundRequest.status !== getInboundStatusType.closed &&
            inboundRequest.status !== getInboundStatusType.received &&
            inboundRequest.status !== getInboundStatusType.partiallyReceived &&
            inboundRequest.status !== getInboundStatusType.inTransit &&
            !(
              (inboundRequest?.extraUnitsIR ||
                inboundRequest?.extraUnitsIr ||
                inboundRequest?.ExtraUnitsIR) &&
              inboundRequest?.status === getInboundStatusType.draft &&
              (userGroup === groupTypes.supplier ||
                userGroup === groupTypes.brand)
            ) &&
            !(
              inboundRequest.status !== getInboundStatusType.draft &&
              userGroup === groupTypes.supplier
            ) &&
            !(
              inboundRequest.status !== getInboundStatusType.draft &&
              (inboundRequest.status !== getInboundStatusType.pending ||
                inboundRequest?.purchase_order?.brand?.type ===
                  "Distribution") &&
              userGroup === groupTypes.brand
            ) && (
              <Stack style={{ position: "absolute", right: 25 }}>
                <CustomButton
                  label="Edit Items"
                  style={{ background: "white" }}
                  onClick={() => {
                    setSelectPurchaseOrderItemsModalVisible(true);
                    setCurrentModalSection("po_items");
                  }}
                />
              </Stack>
            )}
          <CustomTypography
            message="Purchase Order Items:"
            title
            style={{ marginBottom: "35px" }}
          />
          <Stack style={{ width: "100%", alignItems: "center" }}>
            <Stack
              style={{
                display: "flex",
                width: "100%",
                gap: 10,
                position: "relative",
              }}
            >
              {/* Header */}
              <Stack
                style={{
                  flexDirection: "row",
                  fontWeight: "bold",
                  borderBottom: "1px solid #E6E6E6",
                  paddingBottom: 5,
                  fontSize: 10,
                  alignItems: "flex-start",
                }}
              >
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 0,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Internal ID
                </Stack>
                <Stack
                  style={{
                    flex: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  SKU
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  EAN Code
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  Unit Cost
                </Stack>
                <Stack
                  style={{
                    flex: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Colour Description
                </Stack>
                <Stack
                  style={{
                    flex: 2,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Description
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  Vendor Code
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  PO Qty
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  {inboundRequest?.purchase_order?.brand?.type === "Invested"
                    ? "Pending"
                    : "Pending Qty"}
                </Stack>
                <Stack
                  style={{
                    flex: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "right",
                  }}
                >
                  Ready To Ship
                </Stack>
                {showToleranceColumns && (
                  <Stack
                    style={{
                      flex: 1,
                      paddingLeft: 8,
                      paddingRight: 8,
                      minWidth: 0,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      textAlign: "right",
                    }}
                  >
                    {inboundRequest?.purchase_order?.brand?.type === "Invested"
                      ? "Tolerance"
                      : "Max Tolerance"}
                  </Stack>
                )}
                {showToleranceColumns && (
                  <>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {inboundRequest?.purchase_order?.brand?.type ===
                      "Invested"
                        ? "Remaining"
                        : "Remaining Qty"}
                    </Stack>
                    {inboundRequest?.purchase_order?.brand?.type ===
                      "Invested" && (
                      <Stack
                        style={{
                          flex: 1,
                          paddingLeft: 8,
                          paddingRight: 8,
                          minWidth: 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "right",
                        }}
                      >
                        Discrepancy
                      </Stack>
                    )}
                  </>
                )}
                {inboundRequest?.purchase_order?.brand?.type === "Invested" &&
                  !!Object.entries(shortShipmentAnswers)?.length && (
                    <Stack
                      style={{
                        flex: 1,
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 0,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Short Shipment (Yes/No)
                    </Stack>
                  )}
                {showReceived3PLColumn && (
                  <Stack
                    style={{
                      flex: 1,
                      paddingLeft: 8,
                      paddingRight: 8,
                      minWidth: 0,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      textAlign: "right",
                    }}
                  >
                    Received (3PL)
                  </Stack>
                )}
                {showExtraUnitsColumn && (
                  <Stack
                    style={{
                      flex: 1,
                      paddingLeft: 8,
                      paddingRight: 8,
                      minWidth: 0,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      textAlign: "right",
                    }}
                  >
                    Extra Units
                  </Stack>
                )}
              </Stack>

              {/* filteredPOItems */}
              {selectedPOItems.map((POItem, index) => {
                const foundItemsQuantity = quantityValues[POItem?.id];

                return (
                  <Stack
                    key={`po-item-${index}`}
                    style={{
                      flexDirection: "row",
                      fontSize: 15,
                      alignItems: "flex-start",
                      minHeight: "40px",
                    }}
                  >
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 0,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.internalId}
                    </Stack>
                    <Stack
                      style={{
                        flex: 2,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.itemSku}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.eanCode}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {POItem.rate}
                    </Stack>
                    <Stack
                      style={{
                        flex: 2,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.colourDescription}
                    </Stack>
                    <Stack
                      style={{
                        flex: 2,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.description}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {POItem.vendorCode}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {firstQuantityValues.find((x) => x.id === POItem.id)
                        ?.qty ?? POItem.qty}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {POItem.remainingQuantity}
                    </Stack>
                    <Stack
                      style={{
                        flex: 1,
                        paddingLeft: 8,
                        paddingRight: 8,
                        minWidth: 0,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "right",
                      }}
                    >
                      {foundItemsQuantity}
                    </Stack>
                    {index === selectedPOItems.length - 1 && (
                      <Stack
                        style={{
                          position: "absolute",
                          bottom: -20,
                          left: 0,
                          fontSize: 11,
                          fontFamily: "tmwCustomFont",
                          textTransform: "uppercase",
                          gap: 5,
                          flexDirection: "row",
                        }}
                      >
                        <span>Total Quantity:</span>
                        {selectedPOItems.reduce((sum, item) => {
                          const qty = quantityValues[item.id];
                          return (
                            sum + (qty && !isNaN(Number(qty)) ? Number(qty) : 0)
                          );
                        }, 0)}
                      </Stack>
                    )}
                    {showToleranceColumns && (
                      <>
                        <Stack
                          style={{
                            flex: 1,
                            paddingLeft: 8,
                            paddingRight: 8,
                            minWidth: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "right",
                          }}
                        >
                          {(() => {
                            const permittedDisplay = POItem.displayTolerance;
                            const permittedNumeric = Number(
                              POItem.acceptedToleranceAmount ??
                                permittedDisplay ??
                                0,
                            );
                            return <span>{permittedNumeric}</span>;
                          })()}
                        </Stack>
                      </>
                    )}
                    {showToleranceColumns && (
                      <>
                        <Stack
                          style={{
                            flex: 1,
                            paddingLeft: 8,
                            paddingRight: 8,
                            minWidth: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            textAlign: "right",
                          }}
                        >
                          {(() => {
                            const permittedDisplay = POItem.displayTolerance;
                            const permittedNumeric = Number(
                              POItem.acceptedToleranceAmount ??
                                permittedDisplay ??
                                0,
                            );
                            const baseDiff = Number(POItem.difference ?? 0);
                            const currentOld = Number(
                              (originalPoItemsQuantities.length > 0
                                ? originalPoItemsQuantities
                                : inboundRequest?.poItemsQuantities || []
                              ).find((x) => x.po_item_id === POItem.internalId)
                                ?.qty || 0,
                            );
                            const currentNew = Number(
                              quantityValues[POItem.id] ?? currentOld,
                            );
                            const adjustedDiff = Math.max(
                              0,
                              -(baseDiff - currentOld + currentNew),
                            );
                            return <span>{adjustedDiff}</span>;
                          })()}
                        </Stack>
                        {inboundRequest?.purchase_order?.brand?.type ===
                          "Invested" && (
                          <Stack
                            style={{
                              flex: 1,
                              paddingLeft: 8,
                              paddingRight: 8,
                              minWidth: 0,
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              textAlign: "right",
                            }}
                          >
                            {(() => {
                              const permittedDisplay = POItem.displayTolerance;
                              const permittedNumeric = Number(
                                POItem.acceptedToleranceAmount ??
                                  permittedDisplay ??
                                  0,
                              );
                              const currentOld = Number(
                                (originalPoItemsQuantities.length > 0
                                  ? originalPoItemsQuantities
                                  : inboundRequest?.poItemsQuantities || []
                                ).find(
                                  (x) => x.po_item_id === POItem.internalId,
                                )?.qty || 0,
                              );
                              const readyToShip = Number(
                                quantityValues[POItem.id] ?? currentOld,
                              );
                              const pendingQty = Number(
                                firstQuantityValues.find(
                                  (x) => x.id === POItem.id,
                                )?.remainingQuantity ??
                                  POItem.remainingQuantity,
                              );

                              // Calculate Discrepancy
                              let discrepancy = 0;
                              if (readyToShip > pendingQty) {
                                // Overshipment
                                discrepancy =
                                  readyToShip - (pendingQty + permittedNumeric);
                                // Only show if tolerance is exceeded
                                if (discrepancy <= 0) discrepancy = 0;
                              } else if (readyToShip < pendingQty) {
                                // Undershipment
                                discrepancy =
                                  readyToShip - (pendingQty - permittedNumeric);
                                // Only show if tolerance is exceeded
                                if (discrepancy >= 0) discrepancy = 0;
                              }

                              return (
                                <span
                                  style={{
                                    color:
                                      discrepancy !== 0 ? "red" : "inherit",
                                  }}
                                >
                                  {discrepancy}
                                </span>
                              );
                            })()}
                          </Stack>
                        )}
                      </>
                    )}
                    {inboundRequest?.purchase_order?.brand?.type ===
                      "Invested" &&
                      !!Object.entries(shortShipmentAnswers)?.length && (
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "center",
                            paddingLeft: 8,
                            paddingRight: 0,
                            minWidth: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {shortShipmentAnswers[POItem.id] || ""}
                        </Stack>
                      )}
                    {showReceived3PLColumn && (
                      <Stack
                        style={{
                          flex: 1,
                          paddingLeft: 8,
                          paddingRight: 8,
                          minWidth: 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "right",
                        }}
                      >
                        {getBackendReceived3PL(POItem)}
                      </Stack>
                    )}
                    {showExtraUnitsColumn && (
                      <Stack
                        style={{
                          flex: 1,
                          paddingLeft: 8,
                          paddingRight: 8,
                          minWidth: 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "right",
                        }}
                      >
                        {(() => {
                          const extraUnitsValue =
                            getBackendExtraUnitsValue(POItem);
                          return extraUnitsValue === null
                            ? "-"
                            : extraUnitsValue;
                        })()}
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const handleViewAttachment = (attachmentName) => {
    dispatch(assetsThunks.viewItem(attachmentName));
  };

  const renderInputSelect = (
    inputName,
    fileName,
    filePath,
    setFileName,
    fileData,
    setFileData,
    allowedFiles,
    error = "",
    helperText = "",
  ) => {
    const handleFileChange = (event) => {
      const file = event.target.files[0];

      if (file) {
        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (allowedFiles.includes(fileExtension)) {
          setFileName(file.name);
          setFileData(file);
          setFieldsChanged(true);

          // Clear error when file is uploaded
          if (inputName.includes("Packing List")) {
            setPackingListFileError("");
          } else if (inputName.includes("Invoice")) {
            setInvoiceFileError("");
          }
        } else {
          alert("Invalid file type! Please upload a PDF, Excel, or Word file.");

          event.target.value = "";
          setFileName("");
          setFileData(null);
        }
      }
    };

    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const deleteFileEnabled =
      inboundRequest.status !== getInboundStatusType.approved &&
      inboundRequest.status !== getInboundStatusType.received &&
      inboundRequest.status !== getInboundStatusType.closed &&
      inboundRequest.status !== getInboundStatusType.partiallyReceived &&
      inboundRequest.status !== getInboundStatusType.inTransit &&
      !(
        inboundRequest.status !== getInboundStatusType.draft &&
        userGroup === groupTypes.supplier
      ) &&
      !(
        inboundRequest.status !== getInboundStatusType.draft &&
        (inboundRequest.status !== getInboundStatusType.pending ||
          inboundRequest?.purchase_order?.brand?.type === "Distribution") &&
        userGroup === groupTypes.brand
      ) &&
      !(isAuthorizedTmwCrew && !isEditingAuthorized);

    const FileNameDisplay = (fileName, filePath) => {
      const maxLength = 50;

      const truncatedName =
        fileName?.length > maxLength
          ? fileName.slice(0, maxLength) + "..."
          : fileName;

      return (
        <Tooltip title={`View file`}>
          <span
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleViewAttachment(filePath)}
          >
            {truncatedName}
          </span>
        </Tooltip>
      );
    };

    const isSupplierDisabled =
      inboundRequest.status !== getInboundStatusType.draft &&
      userGroup === groupTypes.supplier;

    const isExtraUnitsDraftReadOnlyForPartner =
      (inboundRequest?.extraUnitsIR ||
        inboundRequest?.extraUnitsIr ||
        inboundRequest?.ExtraUnitsIR) &&
      inboundRequest?.status === getInboundStatusType.draft &&
      (userGroup === groupTypes.supplier || userGroup === groupTypes.brand);

    const isAllowedExtraUnitsUploadInput =
      inputName?.includes("Packing List") || inputName?.includes("Invoice");

    const disableInputInExtraUnitsFlow =
      isExtraUnitsDraftReadOnlyForPartner && !isAllowedExtraUnitsUploadInput;

    return (
      <Stack style={{ gap: 5 }}>
        <Stack
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: 10,
          }}
        >
          <CustomButton
            component="label"
            disabled={
              !!(fileName || fileData) ||
              (isAuthorizedTmwCrew && !isEditingAuthorized) ||
              isSupplierDisabled ||
              inboundRequest.status === getInboundStatusType.received ||
              inboundRequest.status ===
                getInboundStatusType.partiallyReceived ||
              inboundRequest.status === getInboundStatusType.inTransit ||
              disableInputInExtraUnitsFlow
            }
            label={
              <>
                {inputName}
                <Input
                  type="file"
                  id={inputName}
                  accept={allowedFiles.map((ext) => `.${ext}`).join(", ")}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </>
            }
            style={{
              width: "fit-content",
              border: error && "1px solid red",
            }}
          />
          {fileName && (
            <Stack
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                margin: "0 10px",
                fontFamily: "tmwCustomFont",
                fontSize: 12,
                color: deleteFileEnabled ? "black" : "gray",
              }}
            >
              {FileNameDisplay(fileName, filePath)}
              {deleteFileEnabled && !disableInputInExtraUnitsFlow && (
                <ButtonBase
                  onClick={() => {
                    setFileName("");
                    setFileData(null);

                    // Reset the file input on close
                    const fileInput = document.getElementById(inputName);
                    if (fileInput) {
                      fileInput.value = "";
                    }

                    // Set error when file is deleted
                    if (inputName.includes("Packing List")) {
                      setPackingListFileError("Packing List is required.");
                    } else if (inputName.includes("Invoice")) {
                      setInvoiceFileError("Invoice is required.");
                    }
                  }}
                >
                  <CloseOutlined style={{ width: "18px" }} />
                </ButtonBase>
              )}
            </Stack>
          )}
        </Stack>
        {!isSupplierDisabled && error && helperText && (
          <span
            style={{
              color: "#d32f2f",
              fontFamily: "tmwCustomFont",
              fontSize: 12,
              marginLeft: 14,
            }}
          >
            {helperText}
          </span>
        )}
      </Stack>
    );
  };

  const renderChoosePOItemsSection = () => {
    const handleQuantityChange = (name, maxValue) => (event) => {
      const value = event.target.value;

      // Allow empty input (so users can delete & retype)
      if (value === "") {
        setQuantityValues((prev) => ({ ...prev, [name]: "" }));
        setFieldsChanged(true);
        return;
      }

      // Reject non-integer input while typing
      if (!/^\d+$/.test(value)) {
        return;
      }

      const num = Number(value);

      // For Invested brands, allow any positive integer
      // For non-Invested brands, allow only integers within range
      if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
        if (num >= 1) {
          setQuantityValues((prev) => ({ ...prev, [name]: num }));
          setFieldsChanged(true);

          // Remove short shipment answer if quantity >= remainingQuantity
          const poItem = filteredPOItems.find((item) => item.id === name);
          if (poItem) {
            const qtyLeft =
              firstQuantityValues.find((x) => x.id === poItem.id)
                ?.remainingQuantity ?? Number(poItem.remainingQuantity);

            if (num >= qtyLeft) {
              setShortShipmentAnswers((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
              });
            }
          }
        }
      } else {
        if (num >= 1 && num <= maxValue) {
          setQuantityValues((prev) => ({ ...prev, [name]: num }));
          setFieldsChanged(true);
        }
      }
    };

    const handleBlur = (name, maxValue) => () => {
      let num = Number(quantityValues[name]);

      // Normalize to an integer and clamp
      if (!Number.isInteger(num)) num = Math.round(num);
      if (isNaN(num) || num < 1) num = 1;

      // For Invested brands, don't cap the maximum
      // For non-Invested brands, cap at maxValue
      if (
        inboundRequest?.purchase_order?.brand?.type !== "Invested" &&
        num > maxValue
      ) {
        num = maxValue;
      }

      setQuantityValues((prev) => ({ ...prev, [name]: num }));
      setFieldsChanged(true);

      // Remove short shipment answer if quantity >= remainingQuantity (for Invested brands)
      if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
        const poItem = filteredPOItems.find((item) => item.id === name);
        if (poItem) {
          const qtyLeft =
            firstQuantityValues.find((x) => x.id === poItem.id)
              ?.remainingQuantity ?? Number(poItem.remainingQuantity);

          if (num >= qtyLeft) {
            setShortShipmentAnswers((prev) => {
              const next = { ...prev };
              delete next[name];
              return next;
            });
          }
        }
      }
    };

    const renderSearchPOItem = () => {
      return (
        <Stack>
          <CustomTextField
            label="Search"
            value={searchPOItem || ""}
            handleChange={(e) => setSearchPOItem(e.target.value)}
            style={{ width: "200px", marginLeft: 25 }}
            inputProps={{
              InputProps: {
                endAdornment: searchPOItem && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchPOItem("")}>
                      <ClearIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      );
    };

    const renderQuantitySection = () => {
      const totalQuantities = POItems.reduce(
        (sum, item) => sum + (item.qty || 0),
        0,
      );

      return (
        <Stack
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            margin: "0 15px",
          }}
        >
          <CustomTypography message="TOTAL QUANTITY INCLUDED IN INBOUND REQUESTS:" />
          <Stack style={{ fontFamily: "tmwCustomFont", fontSize: 14 }}>
            {inboundRequestsItemsQuantityOfPO} / {totalQuantities}
          </Stack>
        </Stack>
      );
    };

    const renderCheckAllItems = () => {
      const message = searchPOItem
        ? "Select all items (With filters)"
        : "Select all items";

      return (
        <CustomFormControl
          control={
            <Checkbox
              style={{
                color: "black",
              }}
            />
          }
          label={<CustomTypography message={message} />}
          checked={selectAllItemsChecked}
          onChange={() => setSelectAllItemsChecked(!selectAllItemsChecked)}
        />
      );
    };

    const renderSearchCount = () => {
      return (
        !!searchPOItem && (
          <Stack
            style={{
              alignItems: "flex-end",
              position: "relative",
              marginBottom: -5,
            }}
          >
            <CustomTypography
              style={{ marginTop: 10, marginRight: 10, position: "absolute" }}
              message={`Found Items: ${filteredPOItems.length}`}
            />
          </Stack>
        )
      );
    };

    const renderExportExcelButton = () => {
      const handleExport = () => {
        const exportData = filteredPOItems
          .filter((POItem) => {
            const isNotCancelled = !POItem.isCancelled;
            const isInIR = inboundRequest.purchase_order_items.some(
              (item) => item.id === POItem.id,
            );

            // For Invested brands, only filter out cancelled items
            if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
              return isNotCancelled;
            }
            // For non-Invested brands, keep original logic
            return (
              isNotCancelled && !(POItem.remainingQuantity === 0 && !isInIR)
            );
          })
          .sort((a, b) => a.internalId - b.internalId)
          .map((POItem) => {
            const isChecked = selectedPOItems.some(
              (item) => item.id === POItem.id,
            );
            // Use the exact value displayed in the table for Selection.
            // If there's no value or it's invalid, export as blank to match the UI.
            const exportedQtyValue = quantityValues[POItem.id];
            const displayRemainingQuantity =
              firstQuantityValues.find((x) => x.id === POItem.id)
                ?.remainingQuantity ?? POItem.remainingQuantity;
            const exportedQty = isChecked
              ? exportedQtyValue === undefined ||
                exportedQtyValue === "" ||
                Number.isNaN(Number(exportedQtyValue))
                ? ""
                : exportedQtyValue
              : displayRemainingQuantity;

            const baseExportData = {
              "Internal ID": POItem.internalId,
              "Item SKU": POItem.itemSku,
              "EAN Code": POItem.eanCode,
              "Unit Cost": POItem.rate,
              "Colour Description": POItem.colourDescription,
              Description: POItem.description,
              "Vendor Code": POItem.vendorCode,
              "PO Qty": POItem.qty,
              [inboundRequest?.purchase_order?.brand?.type === "Invested"
                ? "Pending"
                : "Pending Qty"]: displayRemainingQuantity,
            };

            baseExportData["Ready To Ship"] = exportedQty;

            // Only include additional tolerance columns for Invested brands
            if (showToleranceColumns) {
              const maxTolerance = Number(POItem.acceptedToleranceAmount ?? 0);

              baseExportData[
                inboundRequest?.purchase_order?.brand?.type === "Invested"
                  ? "Tolerance"
                  : "Max Tolerance"
              ] = maxTolerance;
            }

            if (showToleranceColumns) {
              // Calculate Extra Units (Ready To Ship - Pending Qty, minimum 0)
              const readyToShip = Number(exportedQty);
              const pendingQty = Number(POItem.remainingQuantity);
              const extraUnits = Math.max(0, readyToShip - pendingQty);
              baseExportData[
                inboundRequest?.purchase_order?.brand?.type === "Invested"
                  ? "Extra"
                  : "Extra Units"
              ] = extraUnits;
            }

            // Only include additional tolerance columns for Invested brands
            if (showToleranceColumns) {
              const baseDiff = Number(POItem.difference ?? 0);
              const currentOld = Number(
                (inboundRequest?.poItemsQuantities || []).find(
                  (x) => x.po_item_id === POItem.internalId,
                )?.qty || 0,
              );
              const currentNew = Number(
                quantityValues[POItem.id] ?? currentOld,
              );
              const adjustedDiff = Math.max(
                0,
                -(baseDiff - currentOld + currentNew),
              );
              const signedAdjustedDiff = `${
                adjustedDiff > 0 ? "+" : ""
              }${adjustedDiff}`;

              baseExportData[
                inboundRequest?.purchase_order?.brand?.type === "Invested"
                  ? "Remaining"
                  : "Remaining Qty"
              ] = signedAdjustedDiff;

              // Calculate Discrepancy
              const permittedNumeric = Number(
                POItem.acceptedToleranceAmount ?? POItem.displayTolerance ?? 0,
              );
              const readyToShip = Number(exportedQty);
              const pendingQty = Number(POItem.remainingQuantity);

              let discrepancy = 0;
              if (readyToShip > pendingQty) {
                // Overshipment
                discrepancy = readyToShip - (pendingQty + permittedNumeric);
                if (discrepancy <= 0) discrepancy = 0;
              } else if (readyToShip < pendingQty) {
                // Undershipment
                discrepancy = readyToShip - (pendingQty - permittedNumeric);
                if (discrepancy >= 0) discrepancy = 0;
              }

              baseExportData["Discrepancy"] = discrepancy;
            }

            // Invested only: export Short Shipment (Yes/No) for import round-trip
            if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
              baseExportData["Short Shipment (Yes/No)"] =
                shortShipmentAnswers[POItem.id] || "";
            }

            return baseExportData;
          });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PO Items");

        XLSX.writeFile(workbook, "inbound-request-po-items.xlsx");
      };

      return (
        <CustomButton
          style={{ width: "180px" }}
          label={
            <Stack
              style={{
                flexDirection: "row",
                gap: 5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GetApp />
              Export Excel
            </Stack>
          }
          onClick={handleExport}
        />
      );
    };

    // Import Excel Button
    const renderImportExcelButton = () => {
      const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
          const data = await file.arrayBuffer();
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          const importedMap = new Map();
          const cappedItems = [];
          jsonData.forEach((row) => {
            const id = row["Internal ID"] ?? row.Id;
            if (id !== undefined) {
              importedMap.set(Number(id), row);
            }
          });

          setQuantityValues((prev) => {
            const updated = { ...prev };

            filteredPOItems.forEach((item) => {
              if (importedMap.has(Number(item.internalId))) {
                const row = importedMap.get(Number(item.internalId));

                let newQty = Math.round(Number(row["Ready To Ship"]));
                const foundExistingPOItem =
                  inboundRequest.poItemsQuantities?.find(
                    (po) => po.po_item_id === item.internalId,
                  );

                if (
                  inboundRequest?.purchase_order?.brand?.type === "Invested"
                ) {
                  if (!isNaN(newQty) && newQty > 0) {
                    updated[item.id] = newQty;
                  }
                } else {
                  const maxQty = foundExistingPOItem
                    ? item.remainingQuantity + foundExistingPOItem.qty
                    : item.remainingQuantity;
                  if (!isNaN(newQty) && newQty > 0) {
                    if (newQty > maxQty) {
                      cappedItems.push(item);
                    }
                    updated[item.id] = Math.min(newQty, maxQty);
                  }
                }
              }
            });
            return updated;
          });

          const importedSelectedItems = filteredPOItems.filter((item) => {
            if (importedMap.has(Number(item.internalId))) {
              const row = importedMap.get(Number(item.internalId));
              let newQty = Math.round(Number(row["Ready To Ship"]));
              return !isNaN(newQty) && newQty > 0;
            }
            return false;
          });

          setSelectedPOItems(importedSelectedItems);

          // Invested only: import Short Shipment (Yes/No) from Excel
          if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
            setShortShipmentAnswers((prev) => {
              const next = { ...prev };
              filteredPOItems.forEach((item) => {
                if (importedMap.has(Number(item.internalId))) {
                  const row = importedMap.get(Number(item.internalId));
                  const raw =
                    row["Short Shipment (Yes/No)"] ??
                    row["Short Shipment"] ??
                    row["Short_Shipment"] ??
                    "";
                  const normalized =
                    String(raw).trim().toLowerCase() === "yes"
                      ? "Yes"
                      : String(raw).trim().toLowerCase() === "no"
                        ? "No"
                        : "";
                  if (normalized) {
                    next[item.id] = normalized;
                  }
                }
              });
              return next;
            });
          }

          setFieldsChanged(true);
          if (cappedItems.length > 0) {
            toast(
              <div>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <img
                    src={tmwWarningIcon}
                    alt="Warning"
                    style={{ width: 20, height: 20 }}
                  />
                  <span>
                    Some Ready To Ship quantities exceeded the PO quantity and
                    were capped to the PO value.
                  </span>
                </Stack>
              </div>,
              {
                duration: 5000,
                style: {
                  fontFamily: "tmwCustomFont",
                  letterSpacing: 1,
                  fontSize: 12,
                  backgroundColor: "white",
                  color: "black",
                  border: "1px solid red",
                },
              },
            );
          }
        } catch (err) {
          toast.error("Failed to import Excel file");
        } finally {
          event.target.value = "";
        }
      };

      return (
        <CustomButton
          style={{ width: "180px" }}
          component="label"
          label={
            <Stack
              style={{
                flexDirection: "row",
                gap: 5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileUpload />
              Import Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={inputRef}
                style={{ display: "none" }}
                onChange={handleImport}
              />
            </Stack>
          }
        />
      );
    };

    // Check for zero quantities among selected items only
    const hasZeroQtySelected = selectedPOItems.some((item) => {
      const raw = quantityValues[item.id];
      if (raw === "") return true; // treat empty as zero while editing
      const num = Number(raw);
      return !Number.isFinite(num) || num === 0;
    });

    // For Invested brands, require short shipment answers only when qty < remaining
    const hasUnansweredShortForSelected =
      inboundRequest?.purchase_order?.brand?.type === "Invested"
        ? selectedPOItems.some((poItem) => {
            const foundPOItem = POItems.find(
              (poItem2) => poItem2.id === poItem.id,
            );
            const qtyLeft =
              firstQuantityValues.find((x) => x.id === poItem.id)
                ?.remainingQuantity ?? Number(foundPOItem.remainingQuantity);

            const hasExplicitQty =
              Object.prototype.hasOwnProperty.call(quantityValues, poItem.id) &&
              quantityValues[poItem.id] !== "" &&
              !isNaN(Number(quantityValues[poItem.id]));

            const currentNew = hasExplicitQty
              ? Number(quantityValues[poItem.id])
              : qtyLeft;

            const isShort = currentNew < qtyLeft && !poItem.isCancelled;
            if (!isShort) return false; // equal or greater is fine

            const answer = shortShipmentAnswers[poItem.id];
            return !answer || (answer !== "Yes" && answer !== "No");
          })
        : false;

    return (
      <Stack style={{ width: "100%" }}>
        <Stack
          style={{
            width: "100%",
            alignTtems: "center",
            justifyContent: "center",
            flexDirection: "row",
          }}
        >
          <CustomTypography message="Select The Purchase Order Items" title />
        </Stack>
        <Stack
          style={{
            width: "100%",
            gap: 50,
            padding: "50px 25px 25px 25px",
            margin: "25px 0",
            border: "1px solid #E6E6E6",
          }}
        >
          <Stack
            style={{
              gap: 5,
              fontFamily: "tmwCustomFont",
              marginTop: "-25px",
            }}
          >
            <Stack
              style={{
                flexDirection: "row",
                width: "100%",
                justifyContent: "space-between",
                marginBottom: 15,
              }}
            >
              <Stack
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                {renderCheckAllItems()}
              </Stack>
              <Stack
                style={{
                  flexDirection: "row",
                  gap: 15,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {renderExportExcelButton()}
                {renderImportExcelButton()}
                {userGroup !== groupTypes.supplier &&
                  renderImportZedonkButton()}
                {renderSearchPOItem()}
              </Stack>
            </Stack>
            <Stack style={{ border: "1px solid #E6E6E6" }}>
              {/* Column Labels */}
              {(() => {
                const isInvested =
                  inboundRequest?.purchase_order?.brand?.type === "Invested";
                const gridTemplateColumns = isInvested
                  ? showToleranceColumns
                    ? "40px 1fr 1.5fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr"
                    : "40px 1fr 1.5fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1fr"
                  : "40px 1fr 2fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1.5fr 1fr";
                return (
                  <Stack
                    style={{
                      display: "grid",
                      gridTemplateColumns,
                      alignItems: "flex-start",
                      gap: 10,
                      fontWeight: "bold",
                      padding: 10,
                      borderBottom: "1px solid #E6E6E6",
                      userSelect: "none",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontSize: 8,
                    }}
                  >
                    <div />
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 0,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      ID
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Item SKU
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      EAN Code
                    </Stack>
                    {/* numeric */}
                    <Stack
                      style={{
                        textAlign: "right",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Unit Cost
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Colour Description
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Description
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "center",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Vendor Code
                    </Stack>
                    {/* numeric from here on */}
                    <Stack
                      style={{
                        textAlign: "right",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      PO Qty
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "right",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {inboundRequest?.purchase_order?.brand?.type ===
                      "Invested"
                        ? "Pending"
                        : "Pending Qty"}
                    </Stack>
                    <Stack
                      style={{
                        textAlign: "right",
                        paddingLeft: 8,
                        paddingRight: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Ready To Ship
                    </Stack>
                    {showToleranceColumns && (
                      <Stack
                        style={{
                          textAlign: "right",
                          paddingLeft: 8,
                          paddingRight: 8,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {inboundRequest?.purchase_order?.brand?.type ===
                        "Invested"
                          ? "Tolerance"
                          : "Max Tolerance"}
                      </Stack>
                    )}
                    {showToleranceColumns && (
                      <>
                        <Stack
                          style={{
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {inboundRequest?.purchase_order?.brand?.type ===
                          "Invested"
                            ? "Extra"
                            : "Extra Units"}
                        </Stack>
                        <Stack
                          style={{
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {inboundRequest?.purchase_order?.brand?.type ===
                          "Invested"
                            ? "Remaining"
                            : "Remaining Qty"}
                        </Stack>
                        {inboundRequest?.purchase_order?.brand?.type ===
                          "Invested" && (
                          <Stack
                            style={{
                              textAlign: "right",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            Discrepancy
                          </Stack>
                        )}
                      </>
                    )}
                    {isInvested && (
                      <Stack
                        style={{
                          textAlign: "center",
                          paddingLeft: 8,
                          paddingRight: 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        Short Shipment (Yes/No)
                      </Stack>
                    )}
                  </Stack>
                );
              })()}
              {/* Selectable Items */}
              <Stack style={{ maxHeight: "50vh", overflow: "auto" }}>
                {[...filteredPOItems]
                  ?.sort((a, b) => a.internalId - b.internalId)
                  ?.map((POItem, index) => {
                    const isChecked = selectedPOItems.some(
                      (item) => item.id === POItem.id,
                    );

                    const unavailableItem =
                      inboundRequest?.purchase_order?.brand?.type === "Invested"
                        ? false // For Invested brands, no items are unavailable based on quantity
                        : POItem.remainingQuantity === 0 &&
                          !inboundRequest.purchase_order_items.some(
                            (item) => item.id === POItem.id,
                          );

                    const foundExistingPOItem =
                      inboundRequest.poItemsQuantities.find(
                        (item) => item.po_item_id === POItem.internalId,
                      );

                    return (
                      <Stack
                        key={`POItem-${index}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            inboundRequest?.purchase_order?.brand?.type ===
                            "Invested"
                              ? showToleranceColumns
                                ? "40px 1fr 2fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr"
                                : "40px 1fr 2fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1fr 1fr 1fr"
                              : "40px 1fr 2fr 1.5fr 1fr 2fr 2fr 1fr 1fr 1.5fr 1fr",
                          alignItems: "center",
                          gap: 10,
                          margin: "2px 0",
                          cursor: "pointer",
                          padding: "5px",
                          transition: "background-color 0.2s ease-in-out",
                          userSelect: "none",
                          fontSize: 14,
                        }}
                        sx={{
                          color:
                            POItem.isCancelled || unavailableItem
                              ? "lightgrey"
                              : "black",
                          background: POItem.isCancelled
                            ? "rgba(255, 237, 237)"
                            : "",
                          "&:hover": (!POItem.isCancelled ||
                            unavailableItem) && {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        <Checkbox
                          className="hover-checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (!POItem.isCancelled && !unavailableItem) {
                              const currentPOItem =
                                inboundRequest.purchase_order_items.find(
                                  (item) => item?.id === POItem.id,
                                );

                              setFieldsChanged(true);
                              setSelectedPOItems((prev) =>
                                isChecked
                                  ? prev.filter((item) => item.id !== POItem.id)
                                  : [...prev, currentPOItem || POItem],
                              );

                              if (isChecked) {
                                setShortShipmentAnswers((prev) => {
                                  const next = { ...prev };
                                  delete next[POItem.id];
                                  return next;
                                });
                              }
                            }
                          }}
                          style={{
                            color:
                              POItem.isCancelled || unavailableItem
                                ? "lightgrey"
                                : "black",
                            borderRadius: 0,
                            padding: 0,
                            // marginTop: -5,
                            alignSelf: "center",
                          }}
                        />
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "center",
                            paddingLeft: 0,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            alignSelf: "center",
                          }}
                        >
                          {POItem.internalId}
                        </Stack>
                        <Stack
                          style={{
                            flex: 2,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.itemSku}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.eanCode}
                        </Stack>
                        {/* Unit cost */}
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "center",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.rate}
                        </Stack>
                        <Stack
                          style={{
                            flex: 2,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.colourDescription}
                        </Stack>
                        <Stack
                          style={{
                            flex: 2,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.description}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "left",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {POItem.vendorCode}
                        </Stack>
                        {/* PO Qty */}
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {firstQuantityValues.find((x) => x.id === POItem.id)
                            ?.qty ?? POItem.qty}
                        </Stack>
                        {/* Pending Qty */}
                        <Stack
                          style={{
                            flex: 1,
                            textAlign: "right",
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {firstQuantityValues.find((x) => x.id === POItem.id)
                            ?.remainingQuantity ?? POItem.remainingQuantity}
                        </Stack>
                        <Stack
                          style={{
                            alignItems: "flex-end",
                            flex: 1,
                            paddingLeft: 8,
                            paddingRight: 8,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            marginTop: 3,
                          }}
                        >
                          <CustomTextField
                            id={`quantity-${index}`}
                            disabled={
                              POItem.isCancelled ||
                              unavailableItem ||
                              isNaN(quantityValues[POItem.id])
                            }
                            inputProps={{
                              min: 0,
                              max:
                                inboundRequest?.purchase_order?.brand?.type ===
                                "Invested"
                                  ? POItem.qty +
                                    (POItem.acceptedToleranceAmount || 0)
                                  : foundExistingPOItem
                                    ? POItem.remainingQuantity +
                                      foundExistingPOItem.qty
                                    : POItem.remainingQuantity,
                              step: 1,
                            }}
                            handleChange={handleQuantityChange(
                              POItem.id,
                              inboundRequest?.purchase_order?.brand?.type ===
                                "Invested"
                                ? POItem.qty +
                                    (POItem.acceptedToleranceAmount || 0)
                                : foundExistingPOItem
                                  ? POItem.remainingQuantity +
                                    foundExistingPOItem.qty
                                  : POItem.remainingQuantity,
                            )}
                            style={{
                              alignItems: "flex-start",
                              margin: 0,
                              padding: 0,
                              marginTop: -4,
                              paddingTop: 0,
                            }}
                            textFieldStyle={{
                              width: "50px",
                              margin: 0,
                              padding: 0,
                              marginTop: -4,
                              paddingTop: 0,
                            }}
                            onBlur={handleBlur(
                              POItem.id,
                              inboundRequest?.purchase_order?.brand?.type ===
                                "Invested"
                                ? POItem.qty +
                                    (POItem.acceptedToleranceAmount || 0)
                                : foundExistingPOItem
                                  ? POItem.remainingQuantity +
                                    foundExistingPOItem.qty
                                  : POItem.remainingQuantity,
                            )}
                            value={quantityValues[POItem.id] ?? ""}
                            type="number"
                          />
                        </Stack>
                        {showToleranceColumns && (
                          <Stack
                            style={{
                              flex: 1,
                              paddingLeft: 8,
                              paddingRight: 8,
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              textAlign: "right",
                            }}
                          >
                            {(() => {
                              const permittedNumeric = Number(
                                POItem.acceptedToleranceAmount ?? 0,
                              );
                              return <span>{permittedNumeric}</span>;
                            })()}
                          </Stack>
                        )}
                        {showToleranceColumns && (
                          <>
                            {/* Extra Units */}
                            <Stack
                              style={{
                                flex: 1,
                                textAlign: "right",
                                paddingLeft: 8,
                                paddingRight: 8,
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {(() => {
                                const readyToShip = Number(
                                  quantityValues[POItem.id] ??
                                    (
                                      inboundRequest?.poItemsQuantities || []
                                    ).find(
                                      (x) => x.po_item_id === POItem.internalId,
                                    )?.qty ??
                                    POItem.remainingQuantity,
                                );
                                const pendingQty = Number(
                                  firstQuantityValues.find(
                                    (x) => x.id === POItem.id,
                                  )?.remainingQuantity ??
                                    POItem.remainingQuantity,
                                );
                                const extraUnits = Math.max(
                                  0,
                                  readyToShip - pendingQty,
                                );
                                return <span>{extraUnits}</span>;
                              })()}
                            </Stack>
                            {/* Remaining Qty */}
                            <Stack
                              style={{
                                flex: 1,
                                textAlign: "right",
                                paddingLeft: 8,
                                paddingRight: 8,
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {(() => {
                                const baseDiff = Number(POItem.difference ?? 0);
                                const currentOld = Number(
                                  (
                                    inboundRequest?.poItemsQuantities || []
                                  ).find(
                                    (x) => x.po_item_id === POItem.internalId,
                                  )?.qty || 0,
                                );
                                const currentNew = Number(
                                  quantityValues[POItem.id] ?? currentOld,
                                );
                                const adjustedDiff = Math.max(
                                  0,
                                  -(baseDiff - currentOld + currentNew),
                                );
                                return <span>{adjustedDiff}</span>;
                              })()}
                            </Stack>
                            {/* Discrepancy - only for Invested */}
                            {inboundRequest?.purchase_order?.brand?.type ===
                              "Invested" && (
                              <Stack
                                style={{
                                  flex: 1,
                                  textAlign: "left",
                                  paddingLeft: 8,
                                  paddingRight: 8,
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {(() => {
                                  const permittedNumeric = Number(
                                    POItem.acceptedToleranceAmount ??
                                      POItem.displayTolerance ??
                                      0,
                                  );
                                  const currentOld = Number(
                                    (originalPoItemsQuantities.length > 0
                                      ? originalPoItemsQuantities
                                      : inboundRequest?.poItemsQuantities || []
                                    ).find(
                                      (x) => x.po_item_id === POItem.internalId,
                                    )?.qty || 0,
                                  );
                                  const readyToShip = Number(
                                    quantityValues[POItem.id] ?? currentOld,
                                  );
                                  const pendingQty = Number(
                                    firstQuantityValues.find(
                                      (x) => x.id === POItem.id,
                                    )?.remainingQuantity ??
                                      POItem.remainingQuantity,
                                  );

                                  // Calculate Discrepancy
                                  let discrepancy = 0;
                                  if (readyToShip > pendingQty) {
                                    // Overshipment
                                    discrepancy =
                                      readyToShip -
                                      (pendingQty + permittedNumeric);
                                    if (discrepancy <= 0) discrepancy = 0;
                                  } else if (readyToShip < pendingQty) {
                                    // Undershipment
                                    discrepancy =
                                      readyToShip -
                                      (pendingQty - permittedNumeric);
                                    if (discrepancy >= 0) discrepancy = 0;
                                  }

                                  return (
                                    <span
                                      style={{
                                        color:
                                          discrepancy !== 0 ? "red" : "inherit",
                                        textAlign: "right",
                                      }}
                                    >
                                      {discrepancy}
                                    </span>
                                  );
                                })()}
                              </Stack>
                            )}
                          </>
                        )}
                        {inboundRequest?.purchase_order?.brand?.type ===
                          "Invested" && (
                          <Stack style={{ alignItems: "center" }}>
                            {(() => {
                              const currentOld = Number(
                                (inboundRequest?.poItemsQuantities || []).find(
                                  (x) => x.po_item_id === POItem.internalId,
                                )?.qty || 0,
                              );
                              const currentNew = Number(
                                quantityValues[POItem.id] ?? currentOld,
                              );
                              const qtyLeft =
                                firstQuantityValues.find(
                                  (x) => x.id === POItem.id,
                                )?.remainingQuantity ??
                                Number(POItem.remainingQuantity);

                              const showShortShipment =
                                shortShipmentAnswers[POItem.id] ||
                                (selectedPOItems.find(
                                  (selectedPOItem) =>
                                    selectedPOItem.id === POItem.id,
                                ) &&
                                  currentNew < qtyLeft);

                              return (
                                <Stack
                                  style={{
                                    flexDirection: "row",
                                    gap: 5,
                                    flex: 1,
                                  }}
                                >
                                  {showShortShipment && !POItem.isCancelled && (
                                    <select
                                      value={
                                        shortShipmentAnswers[POItem.id] ?? ""
                                      }
                                      onChange={(e) => {
                                        setShortShipmentAnswers((prev) => ({
                                          ...prev,
                                          [POItem.id]: e.target.value,
                                        }));
                                        setFieldsChanged(true);
                                      }}
                                      style={{
                                        border: "1px solid #E6E6E6",
                                        fontFamily:
                                          "tmwCustomFont, Arial, sans-serif",
                                        fontSize: 12,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        backgroundColor: "white",
                                        minWidth: "80px",
                                        font: "inherit",
                                      }}
                                      disabled={
                                        !(
                                          showShortShipment &&
                                          !POItem.isCancelled
                                        )
                                      }
                                    >
                                      <option value="" disabled>
                                        Select
                                      </option>
                                      <option value="Yes">Yes</option>
                                      <option value="No">No</option>
                                    </select>
                                  )}
                                </Stack>
                              );
                            })()}
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
              </Stack>
            </Stack>
            {renderSearchCount()}
          </Stack>
          <Stack
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 10,
              marginTop: "15px",
            }}
          >
            {renderQuantitySection()}
            <Stack style={{ flexDirection: "row", gap: 15 }}>
              <CustomButton
                label="Cancel"
                style={{ width: "100px" }}
                onClick={() => {
                  window.location.reload();
                }}
              />
              <CustomButton
                disabled={
                  !selectedPOItems.length ||
                  hasZeroQtySelected ||
                  hasUnansweredShortForSelected
                }
                label="Accept"
                style={{ width: "100px" }}
                onClick={() => {
                  setCurrentModalSection("remaining_fields");
                  setSelectPurchaseOrderItemsModalVisible(false);

                  const itemQuantities = selectedPOItems.map((item) => ({
                    id: item.id,
                    internalId: item.internalId,
                    quantity: quantityValues[item.id] || 0,
                  }));

                  dispatch(
                    thunks.getInboundRequestQuantities(
                      inboundRequestId,
                      itemQuantities,
                      inboundRequest,
                      selectedPOItems,
                    ),
                  );
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const renderSelectPurchaseOrderItemsSection = () => {
    return (
      <Stack
        style={{
          width: "95vw",
          alignItems: "center",
          alignSelf: "center",
          position: "relative",
        }}
      >
        {currentModalSection === "po_items" &&
          (POItemsLoading ? (
            <CircularProgress
              size={35}
              style={{
                color: "black",
                alignSelf: "center",
              }}
            />
          ) : (
            renderChoosePOItemsSection()
          ))}
      </Stack>
    );
  };

  const renderNotesModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          ref={selectPOModalRef}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "50vw",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: notesModalAction === "draft" ? "orange" : "red",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack
                style={{
                  gap: 35,
                  padding: "25px",
                  alignItems: "center",
                }}
              >
                <Stack style={{ width: "100%" }}>
                  <CustomTypography
                    message={
                      notesModalAction === "draft"
                        ? "Insert the change requests (required):"
                        : "Insert notes for setting to pending (required):"
                    }
                    title
                  />
                </Stack>
                <Stack
                  style={{
                    maxHeight: "50vh",
                    width: "50%",
                    overflow: "auto",
                    fontFamily: "tmwCustomFont",
                    gap: 15,
                  }}
                >
                  <CustomTextField
                    label="Note"
                    value={draftNotes || ""}
                    handleChange={(e) => setDraftNotes(e.target.value)}
                    multiline
                    minRows={3}
                  />
                  <Stack style={{ gap: 10 }}>
                    <CustomButton
                      component="label"
                      label={
                        <>
                          Upload Attachment
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setDraftAttachment(file);
                                setDraftAttachmentName(file.name);
                              }
                            }}
                            style={{ display: "none" }}
                          />
                        </>
                      }
                      style={{ width: "fit-content" }}
                    />
                    {draftAttachmentName && (
                      <Stack
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#666" }}>
                          {draftAttachmentName}
                        </span>
                        <ButtonBase
                          onClick={() => {
                            setDraftAttachment(null);
                            setDraftAttachmentName("");
                          }}
                        >
                          <CloseOutlined style={{ width: "16px" }} />
                        </ButtonBase>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <Stack
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 15,
                  margin: 25,
                }}
              >
                <CustomButton
                  label="Cancel"
                  onClick={() => {
                    setNotesModalVisible(false);
                    setNotesModalAction("draft"); // Reset to default
                  }}
                />
                <CustomButton
                  label="Submit"
                  disabled={
                    inboundRequestStatusChangeDraftLoading ||
                    inboundRequestStatusChangePendingLoading ||
                    !draftNotes?.trim()
                  }
                  loader={
                    (inboundRequestStatusChangeDraftLoading ||
                      inboundRequestStatusChangePendingLoading) && (
                      <CircularProgress size={20} color="inherit" />
                    )
                  }
                  onClick={() => {
                    const formData = new FormData();
                    formData.append("inboundRequestId", inboundRequest.id);
                    formData.append("notes", draftNotes);
                    formData.append("noteId", generateRandomId());
                    if (draftAttachment) {
                      formData.append("attachment", draftAttachment);
                    }

                    // Call appropriate thunk based on action type
                    if (notesModalAction === "draft") {
                      dispatch(thunks.draftInboundRequest(formData));
                    } else if (notesModalAction === "pending") {
                      dispatch(thunks.setToPendingInboundRequest(formData));
                    }

                    // Reset form
                    setDraftNotes("");
                    setDraftAttachment(null);
                    setDraftAttachmentName("");
                    setNotesModalVisible(false);
                    setNotesModalAction("draft"); // Reset to default
                  }}
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderAddNoteFooterButton = () => {
    if (notesView === "list") {
      return (
        <CustomButton
          label="Add New Note"
          style={{ background: "white" }}
          onClick={() => setNotesView("add")}
        />
      );
    } else if (notesView === "add") {
      return (
        <CustomButton
          label="Back to Notes"
          style={{ background: "white" }}
          onClick={() => setNotesView("list")}
        />
      );
    } else if (notesView === "edit") {
      return (
        <CustomButton
          label="Back to Notes"
          style={{ background: "white" }}
          onClick={handleCancelEdit}
        />
      );
    }
    return null;
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditNoteText(note.note || "");
    setEditNoteAttachmentName(note.attachment || "");
    setEditNoteAttachment(null);
    setNotesView("edit");
  };

  const handleSaveEditNote = async () => {
    if (
      !editNoteText.trim() &&
      !editNoteAttachment &&
      !editNoteAttachmentName
    ) {
      toast.error("Please add either text or attachment");
      return;
    }

    setEditNoteLoading(true);
    try {
      const formData = new FormData();
      formData.append("inboundRequestId", inboundRequest.id);
      formData.append("noteId", editingNote.noteId || generateRandomId());
      formData.append("note", editNoteText);

      // Handle attachment: if new file selected, send it; if removed, send flag
      if (editNoteAttachment) {
        formData.append("attachment", editNoteAttachment);
      } else if (editingNote.attachment && !editNoteAttachmentName) {
        // Original note had attachment but user removed it
        formData.append("attachmentDeleted", true);
      }

      await dispatch(thunks.editInboundRequestNote(formData));

      setEditNoteText("");
      setEditNoteAttachment(null);
      setEditNoteAttachmentName("");
      setEditingNote(null);

      // Refresh the inbound request to get updated notes
      dispatch(thunks.getInboundRequest(inboundRequest.id));

      toast.success("Note updated successfully");
      setNotesView("list");
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setEditNoteLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditNoteText("");
    setEditNoteAttachment(null);
    setEditNoteAttachmentName("");
    setEditingNote(null);
    setNotesView("list");
  };

  const renderNotesSystemModal = () => {
    const handleAddNote = async () => {
      if (!newNoteText.trim() && !newNoteAttachment) {
        toast.error("Please add either text or attachment");
        return;
      }

      setAddNoteLoading(true);
      try {
        const formData = new FormData();
        formData.append("noteId", generateRandomId());
        formData.append("inboundRequestId", inboundRequest.id);
        formData.append("note", newNoteText);
        if (newNoteAttachment) {
          formData.append("attachment", newNoteAttachment);
        }

        await dispatch(thunks.addInboundRequestNote(formData));

        setNewNoteText("");
        setNewNoteAttachment(null);
        setNewNoteAttachmentName("");

        // Refresh the inbound request to get updated notes
        dispatch(thunks.getInboundRequest(inboundRequest.id));

        toast.success("Note added successfully");
        setNotesView("list");
      } catch (error) {
        toast.error("Failed to add note");
      } finally {
        setAddNoteLoading(false);
      }
    };

    const sortedNotes = [...(inboundRequest.notes || [])].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "80vw",
            maxHeight: "90vh",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
                maxHeight: "90vh",
                overflow: "hidden",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "#333",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack
                style={{
                  padding: "25px",
                  gap: 20,
                  maxHeight: "calc(90vh - 40px)",
                  overflow: "hidden",
                }}
              >
                <Stack
                  style={{
                    width: "100%",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <CustomTypography
                    message={
                      notesView === "list"
                        ? "Inbound Request Notes"
                        : notesView === "add"
                          ? "Add New Note"
                          : "Edit Note"
                    }
                    title
                    style={{ marginBottom: "10px" }}
                  />
                </Stack>

                {notesView === "list" && (
                  <Stack
                    style={{
                      flex: 1,
                      overflow: "auto",
                      border: "1px solid #E6E6E6",
                    }}
                  >
                    <Stack
                      style={{
                        padding: "15px",
                        borderBottom: "1px solid #E6E6E6",
                        background: "#f8f8f8",
                      }}
                    >
                      <CustomTypography
                        message={`Notes (${sortedNotes.length})`}
                        style={{ fontSize: 14, fontWeight: 600 }}
                      />
                    </Stack>
                    <Stack style={{ maxHeight: "400px", overflow: "auto" }}>
                      {sortedNotes.length === 0 ? (
                        <Stack
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#666",
                          }}
                        >
                          <CustomTypography message="No notes yet" />
                        </Stack>
                      ) : (
                        sortedNotes.map((note, index) => (
                          <Stack
                            key={index}
                            style={{
                              padding: "15px",
                              borderBottom:
                                index !== sortedNotes.length - 1
                                  ? "1px solid #E6E6E6"
                                  : "none",
                              gap: 8,
                            }}
                          >
                            <Stack
                              style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Stack style={{ gap: 5 }}>
                                <Stack
                                  style={{
                                    flexDirection: "row",
                                    gap: 5,
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#666",
                                      fontFamily: "tmwCustomFont",
                                    }}
                                  >
                                    {moment(note.date).format(
                                      "DD/MM/YYYY HH:mm",
                                    )}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#666",
                                      fontFamily: "tmwCustomFont",
                                    }}
                                  >
                                    by {note.addedBy || "Unknown"}
                                  </span>
                                </Stack>
                                {note.note && (
                                  <Stack
                                    style={{
                                      fontFamily: "tmwCustomFont",
                                      fontSize: 14,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {note.note}
                                  </Stack>
                                )}
                              </Stack>
                              {note.attachment && (
                                <Stack
                                  style={{ gap: 5, alignItems: "flex-end" }}
                                >
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#666",
                                      fontFamily: "tmwCustomFont",
                                    }}
                                  >
                                    {note.attachment}
                                  </span>
                                  <CustomButton
                                    label="View Attachment"
                                    onClick={() =>
                                      handleViewAttachment(
                                        "Notes/" + note.attachment,
                                      )
                                    }
                                    style={{
                                      background: "white",
                                      border: "1px solid #E6E6E6",
                                      fontSize: 12,
                                      padding: "5px 10px",
                                      width: "fit-content",
                                    }}
                                  />
                                </Stack>
                              )}
                              {note.addedBy === user.email && (
                                <CustomButton
                                  label="Edit"
                                  onClick={() => handleEditNote(note)}
                                  style={{
                                    background: "white",
                                    border: "1px solid #E6E6E6",
                                    fontSize: 12,
                                    padding: "5px 10px",
                                    width: "fit-content",
                                  }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </Stack>
                )}

                {notesView === "add" && (
                  <Stack
                    style={{
                      border: "1px solid #E6E6E6",
                      padding: "20px",
                      gap: 15,
                    }}
                  >
                    <Stack style={{ gap: 10 }}>
                      <CustomTextField
                        label="Note Text (optional)"
                        value={newNoteText}
                        handleChange={(e) => setNewNoteText(e.target.value)}
                        multiline
                        minRows={3}
                        maxRows={6}
                      />
                      <Stack
                        style={{
                          flexDirection: "row",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <CustomButton
                          component="label"
                          label={
                            <>
                              Upload Attachment
                              <Input
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setNewNoteAttachment(file);
                                    setNewNoteAttachmentName(file.name);
                                  }
                                }}
                                style={{ display: "none" }}
                              />
                            </>
                          }
                          style={{ width: "fit-content" }}
                        />
                        <CustomTypography
                          message="(optional)"
                          style={{ fontSize: 12 }}
                        />
                        {newNoteAttachmentName && (
                          <Stack
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#666" }}>
                              {newNoteAttachmentName}
                            </span>
                            <ButtonBase
                              onClick={() => {
                                setNewNoteAttachment(null);
                                setNewNoteAttachmentName("");
                              }}
                            >
                              <CloseOutlined style={{ width: "16px" }} />
                            </ButtonBase>
                          </Stack>
                        )}
                      </Stack>
                      <Stack
                        style={{
                          flexDirection: "row",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CustomButton
                          label="Insert Note"
                          onClick={handleAddNote}
                          disabled={
                            addNoteLoading ||
                            (!newNoteText.trim() && !newNoteAttachment)
                          }
                          loader={
                            addNoteLoading && (
                              <CircularProgress
                                size={20}
                                color="inherit"
                                style={{ marginLeft: "10px" }}
                              />
                            )
                          }
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}

                {notesView === "edit" && (
                  <Stack
                    style={{
                      border: "1px solid #E6E6E6",
                      padding: "20px",
                      gap: 15,
                    }}
                  >
                    <Stack style={{ gap: 10 }}>
                      <CustomTextField
                        label="Note Text (optional)"
                        value={editNoteText}
                        handleChange={(e) => setEditNoteText(e.target.value)}
                        multiline
                        minRows={3}
                        maxRows={6}
                      />
                      <Stack
                        style={{
                          flexDirection: "row",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <CustomButton
                          component="label"
                          label={
                            <>
                              Upload Attachment
                              <Input
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setEditNoteAttachment(file);
                                    setEditNoteAttachmentName(file.name);
                                  }
                                }}
                                style={{ display: "none" }}
                              />
                            </>
                          }
                          style={{ width: "fit-content" }}
                        />
                        <CustomTypography
                          message="(optional)"
                          style={{ fontSize: 12 }}
                        />
                        {editNoteAttachmentName && (
                          <Stack
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#666" }}>
                              {editNoteAttachmentName}
                            </span>
                            <ButtonBase
                              onClick={() => {
                                setEditNoteAttachment(null);
                                setEditNoteAttachmentName("");
                              }}
                            >
                              <CloseOutlined style={{ width: "16px" }} />
                            </ButtonBase>
                          </Stack>
                        )}
                      </Stack>
                      <Stack
                        style={{
                          flexDirection: "row",
                          justifyContent: "flex-end",
                          gap: 10,
                        }}
                      >
                        {editingNote?.addedBy === user.email && (
                          <CustomButton
                            label="Delete Note"
                            onClick={() => setDeleteNoteModalVisible(true)}
                            style={{ background: "white" }}
                          />
                        )}
                        <CustomButton
                          label="Update Note"
                          onClick={handleSaveEditNote}
                          disabled={
                            editNoteLoading ||
                            (!editNoteText.trim() &&
                              !editNoteAttachment &&
                              !editNoteAttachmentName)
                          }
                          loader={
                            editNoteLoading && (
                              <CircularProgress
                                size={20}
                                color="inherit"
                                style={{ marginLeft: "10px" }}
                              />
                            )
                          }
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Stack>
              <Stack
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 15,
                  margin: "0 25px 25px 0",
                }}
              >
                {renderAddNoteFooterButton()}
                <CustomButton
                  label="Close"
                  onClick={() => {
                    setNotesView("list");
                    setNotesSystemModalVisible(false);
                  }}
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderImportZedonkButton = () => {
    const handleButtonClick = () => {
      zedonkFileInputRef.current?.click();
    };

    const convertKeysToCamelCase = (data) => {
      return data.map((row) => {
        const newRow = {};
        Object.keys(row).forEach((key) => {
          newRow[camelCase(key)] = row[key];
        });
        return newRow;
      });
    };

    const handleFileChange = async (event) => {
      setImportZedonkParsing(true);

      const file = event.target.files[0];
      if (!file) return;

      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 0,
          defval: "",
        });

        const parsedData = convertKeysToCamelCase(jsonData);
        const filteredData = parsedData.map((row) => ({
          eanCode: row.barcode,
          quantity: row.inTransitQty,
        }));

        const quantityMap = new Map(
          filteredData.map((item) => [item.eanCode, item.quantity]),
        );

        const matchingObjects = POItems.filter((item) => {
          const hasEanCode = quantityMap.has(item.eanCode);
          const isNotCancelled = item.isCancelled === false;

          // For Invested brands, only check if not cancelled
          if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
            return hasEanCode && isNotCancelled;
          }
          // For non-Invested brands, keep original logic
          return hasEanCode && isNotCancelled;
        })
          .map((item) => {
            const requestedQty = quantityMap.get(item.eanCode);

            const existingItem = selectedPOItems.find(
              (POItem) => POItem.eanCode === item.eanCode,
            );

            let cappedQty = 0;

            // For Invested brands, don't cap the quantity
            if (inboundRequest?.purchase_order?.brand?.type === "Invested") {
              if (existingItem) {
                return {
                  ...item,
                  remainingQuantity: requestedQty,
                };
              } else {
                return {
                  ...item,
                  remainingQuantity: requestedQty,
                };
              }
            }

            // For non-Invested brands, keep original logic
            if (existingItem) {
              if (existingItem.remainingQuantity > 0) {
                cappedQty = Math.min(
                  requestedQty,
                  existingItem.remainingQuantity,
                );

                return {
                  ...item,
                  remainingQuantity: cappedQty,
                };
              }
              return null;
            } else {
              if (item.remainingQuantity > 0) {
                cappedQty = Math.min(requestedQty, item.remainingQuantity);

                return {
                  ...item,
                  remainingQuantity: cappedQty,
                };
              }
              return null;
            }
          })
          .filter(Boolean);

        // Select the imported items
        setSelectedPOItems(matchingObjects);

        setFieldsChanged(true);

        toast(
          <div>
            <Stack>Zedonk file imported successfully</Stack>
          </div>,
          {
            duration: 3000,
            style: {
              fontFamily: "tmwCustomFont",
              letterSpacing: 1,
              fontSize: 12,
              backgroundColor: "white",
              color: "black",
              border: "1px solid green",
            },
          },
        );

        setImportZedonkParsing(false);
      } catch (error) {
        console.error("Parsing or uploading failed:", error);

        toast(
          <div>
            <Stack>Zedonk file import error</Stack>
          </div>,
          {
            duration: 3000,
            style: {
              fontFamily: "tmwCustomFont",
              letterSpacing: 1,
              fontSize: 12,
              backgroundColor: "white",
              color: "black",
              border: "1px solid red",
            },
          },
        );

        setImportZedonkParsing(false);
      }
    };

    return (
      inboundRequest?.purchase_order?.brand?.type === "Invested" && (
        <Stack alignItems="flex-end">
          <CustomButton
            style={{ width: "200px" }}
            label={
              <Stack
                style={{
                  flexDirection: "row",
                  gap: 5,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PlaylistAdd />
                Import Zedonk
              </Stack>
            }
            onClick={handleButtonClick}
          />
          <input
            type="file"
            ref={zedonkFileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </Stack>
      )
    );
  };

  // Validation function to check if all short shipment items are answered
  const validateShortShipmentAnswers = () => {
    if (inboundRequest?.purchase_order?.brand?.type !== "Invested") {
      return true; // Only validate for Invested brands
    }

    if (POItems.length === 0) return true;

    // Check if there are any short shipment items that need answers
    const hasUnansweredShortShipments = selectedPOItems.some((POItem) => {
      const foundPOItem = POItems.find((poItem2) => poItem2.id === POItem.id);
      const qtyLeft =
        firstQuantityValues.find((x) => x.id === POItem.id)
          ?.remainingQuantity ?? Number(foundPOItem.remainingQuantity);

      // If user hasn't explicitly set a quantity yet, treat as not short-shipped
      const hasExplicitQty =
        Object.prototype.hasOwnProperty.call(quantityValues, POItem.id) &&
        quantityValues[POItem.id] !== "" &&
        !isNaN(Number(quantityValues[POItem.id]));

      const currentNew = hasExplicitQty
        ? Number(quantityValues[POItem.id])
        : qtyLeft;

      const showShortShipment = currentNew < qtyLeft;

      if (showShortShipment && !POItem.isCancelled) {
        const answer = shortShipmentAnswers[POItem.id];
        return !answer || (answer !== "Yes" && answer !== "No");
      }
      return false;
    });

    return !hasUnansweredShortShipments;
  };

  const renderIRActionButtons = () => {
    const filesPresent =
      (packingListFileData instanceof File || packingListFileName) &&
      (invoiceFileData instanceof File || invoiceFileName);

    const invoiceNetTotalNotChanged =
      Number(invoiceNetTotal) === inboundRequest.invoiceNetTotal;

    const incotermNotChanged = incoterm === inboundRequest.incoterm;

    // Check if Save Changes button would be enabled (has unsaved changes)
    const hasUnsavedChanges =
      (fieldsChanged ||
        !invoiceNetTotalNotChanged ||
        !incotermNotChanged ||
        pickupDetailsChanged) &&
      !(
        (userGroup === groupTypes.supplier &&
          inboundRequest?.status !== getInboundStatusType.draft) ||
        (userGroup === groupTypes.brand &&
          inboundRequest?.status !== getInboundStatusType.draft &&
          (inboundRequest?.status !== getInboundStatusType.pending ||
            inboundRequest?.purchase_order?.brand?.type === "Distribution"))
      );

    const disabled =
      !!(filesPresent && fieldsChanged) || !!invoiceNetTotalError;
    const shortShipmentValidationPassed = validateShortShipmentAnswers();

    const totalNumberOfCartonsValid = !(
      totalNumberOfCartons === null ||
      totalNumberOfCartons === undefined ||
      totalNumberOfCartons === "" ||
      isNaN(Number(totalNumberOfCartons)) ||
      Number(totalNumberOfCartons) < 0
    );

    const totalNumberOfPalletsValid = !(
      totalNumberOfPallets === null ||
      totalNumberOfPallets === undefined ||
      totalNumberOfPallets === "" ||
      isNaN(Number(totalNumberOfPallets)) ||
      Number(totalNumberOfPallets) < 0
    );

    const canAddExtraUnits =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest?.purchase_order?.brand?.type === "Invested" &&
      (inboundRequest?.status === getInboundStatusType.inTransit ||
        inboundRequest?.status === getInboundStatusType.partiallyReceived ||
        inboundRequest?.status === getInboundStatusType.received);

    const canMarkPartiallyReceivedInbound =
      userGroup === groupTypes.tomorrowCrew &&
      ((inboundRequest?.status === getInboundStatusType.partiallyReceived &&
        !!uploadCNForMissingItemsValue &&
        !!missingItemsCreditNoteAttachmentPath) ||
        (inboundRequest?.status === getInboundStatusType.partiallyReceived &&
          !uploadCNForMissingItemsValue));

    const canCloseReceivedInbound =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest?.status === getInboundStatusType.received;

    const renderSubmitRequestButton = () => {
      return (
        inboundRequest.status !== getInboundStatusType.pending &&
        inboundRequest.status !== getInboundStatusType.authorized &&
        inboundRequest.status !== getInboundStatusType.approved &&
        inboundRequest.status !== getInboundStatusType.inTransit &&
        inboundRequest.status !== getInboundStatusType.received &&
        inboundRequest.status !== getInboundStatusType.closed &&
        inboundRequest?.status !== getInboundStatusType.partiallyReceived && (
          <CustomButton
            style={{
              width: "200px",
              background: "white",
            }}
            redButton={submitInboundRequestError}
            label="Submit Request"
            disabled={
              isExtraUnitsDraftReadOnlyForPartner
                ? !filesPresent
                : (inboundRequest?.purchase_order?.brand?.type === "Invested" &&
                    !invoiceNetTotal &&
                    !shortShipmentValidationPassed) ||
                  (inboundRequest?.purchase_order?.brand?.type ===
                    "Distribution" &&
                    !invoiceNetTotal) ||
                  !!invoiceNetTotalError ||
                  !filesPresent ||
                  !invoiceNumber ||
                  !packingListNumber ||
                  !totalNumberOfCartonsValid ||
                  !totalNumberOfPalletsValid ||
                  !totalUnits ||
                  !!totalUnitsError ||
                  !boxDimensions ||
                  !palletDimensions ||
                  !totalGrossWeight ||
                  !!totalGrossWeightError ||
                  !totalNetWeight ||
                  !!totalNetWeightError ||
                  !contentDetails ||
                  !pickupCountry ||
                  !pickupCity ||
                  !pickupZipCode ||
                  !pickupAddress ||
                  !pickupCompanyName ||
                  (!isDDPIncoterm &&
                    (!readyDateForPickup ||
                      !openingHours ||
                      !contactName ||
                      !phoneNumber ||
                      !supplierEmailContact)) ||
                  (userGroup === groupTypes.tomorrowCrew && !incoterm) ||
                  (userGroup === groupTypes.supplier &&
                    inboundRequest.status !== getInboundStatusType.draft) ||
                  (userGroup === groupTypes.brand &&
                    inboundRequest.status !== getInboundStatusType.draft &&
                    (inboundRequest.status !== getInboundStatusType.pending ||
                      inboundRequest?.purchase_order?.brand?.type ===
                        "Distribution"))
            }
            onClick={handleSubmitInboundRequestFromEdit}
            loader={
              submitInboundRequestLoading && (
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginLeft: "10px" }}
                />
              )
            }
          />
        )
      );
    };

    const renderSaveChangesButton = () => {
      return (
        <CustomButton
          style={{
            width: "200px",
            background: "white",
          }}
          redButton={submitInboundRequestError}
          label="Save Changes"
          disabled={
            isReadOnlyStatus ||
            (!fieldsChanged &&
              invoiceNetTotalNotChanged &&
              incotermNotChanged &&
              !pickupDetailsChanged) ||
            (userGroup === groupTypes.supplier &&
              inboundRequest?.status !== getInboundStatusType.draft) ||
            (userGroup === groupTypes.brand &&
              inboundRequest?.status !== getInboundStatusType.draft &&
              (inboundRequest?.status !== getInboundStatusType.pending ||
                inboundRequest?.purchase_order?.brand?.type === "Distribution"))
          }
          onClick={() => handleEditInboundRequest(true)}
          loader={
            saveChangesLoading && (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginLeft: "10px" }}
              />
            )
          }
        />
      );
    };

    return (
      <Stack
        style={{
          flexDirection: "row",
          gap: 15,
          width: "93vw",
          justifyContent: "flex-end",
          marginBottom: "10px",
        }}
      >
        <Stack style={{ gap: 15 }}>
          <Stack style={{ flexDirection: "row", gap: 15 }}>
            {userGroup === groupTypes.tomorrowCrew &&
              !!inboundRequest.history && (
                <CustomButton
                  label="History"
                  onClick={() => setHistoryModalVisible(true)}
                  disabled={
                    inboundRequestStatusChangeSubmitLoading ||
                    inboundRequestStatusChangeDraftLoading ||
                    inboundRequestStatusChangePendingLoading ||
                    inboundRequestStatusChangeApproveLoading
                  }
                  style={{ width: "150px" }}
                />
              )}
            {userGroup === groupTypes.tomorrowCrew && (
              <CustomButton
                label="Emails"
                style={{ width: "120px" }}
                onClick={handleOpenEmailHistoryModal}
              />
            )}
            <CustomButton
              label="Notes"
              onClick={() => setNotesSystemModalVisible(true)}
              disabled={
                (isReadOnlyStatus && userGroup !== groupTypes.tomorrowCrew) ||
                inboundRequestStatusChangeSubmitLoading ||
                inboundRequestStatusChangeDraftLoading ||
                inboundRequestStatusChangePendingLoading ||
                inboundRequestStatusChangeApproveLoading
              }
              style={{ width: "150px" }}
            />
            {canMarkPartiallyReceivedInbound && (
              <CustomButton
                label="Mark as Received"
                onClick={() => {
                  setCloseInboundRequestAction("mark_received");
                  setCloseIRModalOpened(true);
                }}
                disabled={
                  closeInboundRequestLoading ||
                  inboundRequestStatusChangeSubmitLoading ||
                  inboundRequestStatusChangeDraftLoading ||
                  inboundRequestStatusChangePendingLoading ||
                  inboundRequestStatusChangeApproveLoading
                }
                style={{ width: "200px" }}
              />
            )}
            {canCloseReceivedInbound && (
              <CustomButton
                label="Close Inbound Request"
                onClick={() => {
                  setCloseInboundRequestAction("close");
                  setCloseIRModalOpened(true);
                }}
                disabled={
                  closeInboundRequestLoading ||
                  inboundRequestStatusChangeSubmitLoading ||
                  inboundRequestStatusChangeDraftLoading ||
                  inboundRequestStatusChangePendingLoading ||
                  inboundRequestStatusChangeApproveLoading
                }
                style={{ width: "250px" }}
              />
            )}
            {canAddExtraUnits && (
              <CustomButton
                label="EXTRA UNITS"
                onClick={handleOpenExtraUnitsModal}
                disabled={
                  !selectedPOItems.length ||
                  inboundRequestStatusChangeSubmitLoading ||
                  inboundRequestStatusChangeDraftLoading ||
                  inboundRequestStatusChangePendingLoading ||
                  inboundRequestStatusChangeApproveLoading
                }
                style={{ width: "150px", background: "white" }}
              />
            )}
            {(inboundRequest?.status === getInboundStatusType.approved ||
              inboundRequest?.status === getInboundStatusType.inTransit ||
              inboundRequest?.status ===
                getInboundStatusType.partiallyReceived ||
              inboundRequest?.status === "Received" ||
              inboundRequest?.status === getInboundStatusType.closed) && (
              <Tooltip title="Pick-up details">
                <IconButton
                  onClick={() =>
                    (window.location.href =
                      paths.dashboard.inbound.requests.pickUp +
                      inboundRequestId +
                      "?from=details")
                  }
                  disabled={
                    inboundRequestStatusChangeSubmitLoading ||
                    inboundRequestStatusChangeDraftLoading ||
                    inboundRequestStatusChangePendingLoading ||
                    inboundRequestStatusChangeApproveLoading
                  }
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid #E6E6E6",
                    borderRadius: "25px",
                  }}
                >
                  <LocalShippingIcon style={{ color: "black" }} />
                </IconButton>
              </Tooltip>
            )}
            {((inboundRequest?.status !== getInboundStatusType.approved &&
              inboundRequest?.status !== getInboundStatusType.authorized &&
              inboundRequest?.status !== getInboundStatusType.inTransit &&
              inboundRequest?.status !== getInboundStatusType.received &&
              inboundRequest?.status !== getInboundStatusType.closed &&
              inboundRequest?.status !==
                getInboundStatusType.partiallyReceived) ||
              (inboundRequest?.status === getInboundStatusType.authorized &&
                isEditingAuthorized)) &&
              renderSaveChangesButton()}
            {renderSubmitRequestButton()}
            {(inboundRequest?.status === getInboundStatusType.pending ||
              inboundRequest?.status === getInboundStatusType.authorized) && (
              <Stack style={{ flexDirection: "row", gap: 15 }}>
                {(userGroup === groupTypes.tomorrowCrew ||
                  (userGroup === groupTypes.brand &&
                    inboundRequest?.status !==
                      getInboundStatusType.authorized &&
                    !(
                      inboundRequest?.purchase_order?.brand?.type ===
                        "Distribution" &&
                      inboundRequest.status === getInboundStatusType.pending &&
                      userGroup === groupTypes.brand
                    ))) && (
                  <Stack style={{ flexDirection: "row", gap: 10 }}>
                    <CustomButton
                      label={
                        inboundRequest?.purchase_order?.brand?.type ===
                          "Invested" &&
                        inboundRequest?.status === getInboundStatusType.pending
                          ? "Authorize Inbound Request"
                          : "Approve Inbound Request"
                      }
                      onClick={() => setApproveIRModalOpened(true)}
                      disabled={
                        hasUnsavedChanges ||
                        (inboundRequest?.purchase_order?.brand?.type ===
                          "Invested" &&
                        (inboundRequest?.status ===
                          getInboundStatusType.pending ||
                          inboundRequest?.status ===
                            getInboundStatusType.authorized)
                          ? false
                          : disabled) ||
                        inboundRequestStatusChangeSubmitLoading ||
                        inboundRequestStatusChangeDraftLoading ||
                        inboundRequestStatusChangePendingLoading ||
                        inboundRequestStatusChangeApproveLoading ||
                        (userGroup === groupTypes.tomorrowCrew && !incoterm) ||
                        !!incotermError ||
                        !!invoiceNetTotalError ||
                        (inboundRequest?.purchase_order?.brand?.type ===
                          "Invested" &&
                          !invoiceNetTotal) ||
                        !totalUnits ||
                        !!totalUnitsError
                      }
                      loader={
                        inboundRequestStatusChangeApproveLoading && (
                          <CircularProgress
                            size={20}
                            color="inherit"
                            style={{ marginLeft: "10px" }}
                          />
                        )
                      }
                    />
                  </Stack>
                )}
                {inboundRequest.status === getInboundStatusType.authorized &&
                  userGroup === groupTypes.tomorrowCrew &&
                  userGroup !== groupTypes.brand && (
                    <Stack>
                      <CustomButton
                        label="Set Back to Pending"
                        style={{ background: "white" }}
                        onClick={handleSetToPendingInboundRequest}
                        disabled={
                          disabled ||
                          inboundRequestStatusChangeSubmitLoading ||
                          inboundRequestStatusChangeDraftLoading ||
                          inboundRequestStatusChangePendingLoading ||
                          inboundRequestStatusChangeApproveLoading
                        }
                        loader={
                          inboundRequestStatusChangePendingLoading && (
                            <CircularProgress
                              size={20}
                              color="inherit"
                              style={{ marginLeft: "10px" }}
                            />
                          )
                        }
                      />
                    </Stack>
                  )}
                <Stack>
                  {(userGroup === groupTypes.tomorrowCrew ||
                    (inboundRequest.status !==
                      getInboundStatusType.authorized &&
                      userGroup === groupTypes.brand &&
                      inboundRequest?.purchase_order?.brand?.type ===
                        "Invested")) && (
                    <CustomButton
                      label="Set Back to Draft"
                      style={{ background: "white" }}
                      onClick={handleDraftInboundRequest}
                      disabled={
                        disabled ||
                        inboundRequestStatusChangeSubmitLoading ||
                        inboundRequestStatusChangeDraftLoading ||
                        inboundRequestStatusChangePendingLoading ||
                        inboundRequestStatusChangeApproveLoading
                      }
                      loader={
                        inboundRequestStatusChangeDraftLoading && (
                          <CircularProgress
                            size={20}
                            color="inherit"
                            style={{ marginLeft: "10px" }}
                          />
                        )
                      }
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const renderInvoiceNetTotalSection = () => {
    const currency =
      inboundRequest?.purchase_order?.currency === "EUR"
        ? "€"
        : inboundRequest?.purchase_order?.currency === "GBP"
          ? "£"
          : inboundRequest?.purchase_order?.currency === "USD"
            ? "$"
            : inboundRequest?.purchase_order?.currency === "JPY"
              ? "¥"
              : "";

    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const disabledInput =
      disabledButton ||
      (userGroup === groupTypes.supplier &&
        inboundRequest.status !== getInboundStatusType.draft) ||
      (inboundRequest?.purchase_order?.brand?.type === "Distribution" &&
        inboundRequest.status === getInboundStatusType.pending &&
        userGroup === groupTypes.brand) ||
      (isAuthorizedTmwCrew && !isEditingAuthorized);

    return (
      <Stack
        style={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <Stack
          style={{
            width: "80%",
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <CustomTypography
            message="Invoice Net Total:"
            style={{ fontSize: 11, width: "240px" }}
          />
          <CustomTextField
            style={{ width: "100%" }}
            value={invoiceNetTotal}
            inputProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <span
                      style={{ color: disabledInput ? "#b0b0b0" : "black" }}
                    >
                      {currency}
                    </span>
                  </InputAdornment>
                ),
              },
            }}
            handleChange={(e) => {
              invoiceNetTotalValidation(e.target.value);
            }}
            error={
              !isExtraUnitsDraftReadOnlyForPartner && !!invoiceNetTotalError
            }
            helperText={
              !isExtraUnitsDraftReadOnlyForPartner ? invoiceNetTotalError : ""
            }
            disabled={disabledInput}
          />
        </Stack>
        {submitCurrencyError && !isExtraUnitsDraftReadOnlyForPartner && (
          <Stack
            style={{
              fontFamily: "tmwCustomFont",
              fontSize: 11,
              color: "red",
            }}
          >
            <span>
              Invoice Net Total does not match the calculated total for the
              selected items.
            </span>
            <span>
              Please revise the total and possibly correct also the invoice.
            </span>
          </Stack>
        )}
        {(userGroup === groupTypes.tomorrowCrew ||
          (userGroup === groupTypes.brand &&
            inboundRequest?.purchase_order?.brand?.type === "Invested")) && (
          <Stack
            style={{
              width: "80%",
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
            }}
          >
            <CustomTypography
              message="Calculated Net Total:"
              style={{ fontSize: 11, width: "300px" }}
            />
            <CustomTextField
              style={{ width: "100%" }}
              value={inboundRequest.calculatedNetTotal}
              inputProps={{
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ color: "#b0b0b0" }}>{currency}</span>
                    </InputAdornment>
                  ),
                },
              }}
              disabled
            />
          </Stack>
        )}
        {userGroup === groupTypes.tomorrowCrew && (
          <Autocomplete
            id="autocomplete-incoterm"
            name="autocomplete-incoterm"
            disableClearable
            disabled={disabledInput}
            options={["EXW", "DDP"]}
            value={incoterm || null}
            style={{ width: "80%" }}
            onChange={(e, value) => {
              if (inboundRequest.status !== getInboundStatusType.pending) {
                setFieldsChanged(true);
              }

              setIncoterm(value || "");
            }}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                <div
                  style={{
                    fontFamily: "tmwCustomFont",
                    fontSize: 13,
                  }}
                >
                  {option}
                </div>
              </li>
            )}
            renderInput={(params) => (
              <Stack
                style={{
                  width: "100%",
                  height: "100%",
                  padding: "10px 0",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    width: "130px",
                    fontFamily: "tmwCustomFont",
                    fontSize: 11,
                    color: "black",
                    letterSpacing: 3,
                    userSelect: "none",
                    textTransform: "uppercase",
                  }}
                >
                  Incoterm:
                </span>
                <TextField
                  {...params}
                  sx={{
                    "& .MuiInput-underline:before": {
                      borderBottom: incotermError
                        ? "1px solid red"
                        : "1px solid #E6E6E6",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: incotermError
                        ? "2px solid red"
                        : "2px solid #333",
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "tmwCustomFont",
                      paddingLeft: "10px",
                    },
                    width: "200px",
                  }}
                  error={!!incotermError}
                  helperText={incotermError}
                  id="incoterm"
                  name="incoterm"
                  fullWidth
                  value={incoterm || ""}
                  variant="standard"
                />
              </Stack>
            )}
          />
        )}
        {renderTotalUnitsSection()}
      </Stack>
    );
  };

  const renderTotalUnitsSection = () => {
    const calculatedTotalUnits = selectedPOItems.reduce((sum, item) => {
      const qty = quantityValues[item.id];
      return sum + (qty && !isNaN(Number(qty)) ? Number(qty) : 0);
    }, 0);

    const isAuthorizedTmwCrew =
      userGroup === groupTypes.tomorrowCrew &&
      inboundRequest.status === getInboundStatusType.authorized;

    const disabled =
      disabledButton ||
      (userGroup === groupTypes.supplier &&
        inboundRequest.status !== getInboundStatusType.draft) ||
      (inboundRequest?.purchase_order?.brand?.type === "Distribution" &&
        inboundRequest.status === getInboundStatusType.pending &&
        userGroup === groupTypes.brand) ||
      (isAuthorizedTmwCrew && !isEditingAuthorized);

    return (
      <Stack
        style={{
          padding: "10px 0",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <CustomTypography
          message="Total units (qty):"
          style={{ fontSize: 11, width: "175px" }}
        />
        <CustomTextField
          style={{ width: "200px" }}
          value={totalUnits}
          type="number"
          handleChange={(e) => {
            const val = e.target.value;
            if (val !== totalUnits) {
              setFieldsChanged(true);
            }
            if (val === "") {
              setTotalUnits("");
              setTotalUnitsError("");
              return;
            }
            if (!/^\d+$/.test(val)) return;
            setTotalUnits(val);
            const num = Number(val);
            if (num !== calculatedTotalUnits) {
              setTotalUnitsError("Value must equal the Total Quantity.");
            } else {
              setTotalUnitsError("");
            }
          }}
          error={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !disabled &&
            (!!totalUnitsError || !totalUnits)
          }
          helperText={
            !isExtraUnitsDraftReadOnlyForPartner &&
            !disabled &&
            (totalUnitsError ||
              (!totalUnits && "Total units (qty) is required."))
          }
          disabled={disabled}
        />
      </Stack>
    );
  };

  const renderHistorySection = () => {
    const renderHistoryItem = (item, index) => {
      const getHistoryIcon = (type) => {
        if (type === "Draft" || type === "Pending") {
          return <UndoIcon />;
        } else if (type === "Creation") {
          return <AddIcon />;
        } else if (type === "Edit") {
          return <EditIcon />;
        } else if (type === "Submit") {
          return <HourglassTopIcon />;
        } else if (type === "Pickup") {
          return <LocalShippingIcon />;
        } else if (type === "In Transit") {
          return <RouteIcon />;
        } else if (type === "Pickup Photos") {
          return <PhotoCameraIcon />;
        } else if (type === "Approval") {
          return <CheckCircleIcon style={{ color: "green" }} />;
        } else if (type === "Error") {
          return <Error style={{ color: "red" }} />;
        } else if (type === "Authorization") {
          return <ContactEmergencyIcon />;
        }

        return <InfoOutlinedIcon />;
      };

      return (
        <Stack key={"inbound-request-history-log" + index}>
          <TimelineItem
            sx={{
              "&::before": {
                display: "none",
              },
            }}
          >
            <TimelineSeparator>
              <TimelineDot
                sx={{
                  border: 0,
                  p: 0,
                }}
              >
                <Avatar>
                  <SvgIcon>{getHistoryIcon(item.type)}</SvgIcon>
                </Avatar>
              </TimelineDot>
              {index !== inboundRequest.history.length - 1 && (
                <TimelineConnector
                  sx={{
                    minHeight: 30,
                  }}
                />
              )}
            </TimelineSeparator>
            <TimelineContent>
              <Stack
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  height:
                    index !== inboundRequest.history.length - 1 ? "65%" : "85%",
                }}
              >
                <Stack
                  style={{
                    flex: 2,
                    textAlign: "left",
                    margin: "0 15px",
                  }}
                >
                  {item.message}
                </Stack>
                <Stack style={{ flex: 1, textAlign: "left" }}>
                  {moment(item.date).format("DD/MM/YYYY HH:mm")}
                </Stack>
                <Stack style={{ flex: 1, textAlign: "left" }}>{item.by}</Stack>
              </Stack>
            </TimelineContent>
          </TimelineItem>
        </Stack>
      );
    };

    return (
      <Stack
        style={{
          height: "100%",
          width: "100%",
          padding: "35px 50px",
          gap: 15,
        }}
      >
        <Stack
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 10,
          }}
        >
          <CustomTypography message="Request History" title />
        </Stack>
        <Stack
          style={{
            fontFamily: "tmwCustomFont",
            maxHeight: "40vh",
            overflow: "auto",
            scrollbarColor: "lightgrey transparent",
          }}
        >
          <Timeline
            sx={{
              m: 0,
              p: 0,
            }}
          >
            {[...(inboundRequest.history || [])]
              .sort(
                (a, b) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime(),
              )
              .map((history, index) => renderHistoryItem(history, index))}
          </Timeline>
        </Stack>
      </Stack>
    );
  };

  const renderHistoryModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          ref={selectPOModalRef}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "80vw",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "green",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              {renderHistorySection()}
              <Stack
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 15,
                  margin: "-15px 25px 25px 0",
                }}
              >
                <CustomButton
                  label="Close"
                  onClick={() => setHistoryModalVisible(false)}
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderConfirmIRModal = (
    handleConfirm,
    handleCancel,
    message1,
    submitLoader,
  ) => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "50vw",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "green",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <Stack style={{ padding: "35px", gap: 15 }}>
                  {message1 && <CustomTypography message={message1} />}
                </Stack>
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                    margin: "-15px 25px 25px 0",
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={handleCancel}
                    disabled={submitLoader}
                  />
                  <CustomButton
                    label="Confirm"
                    onClick={handleConfirm}
                    disabled={submitLoader}
                    loader={
                      submitLoader && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderApproveIRModal = (
    handleConfirm,
    handleCancel,
    message1,
    message2,
    submitLoader,
    isApprove,
  ) => {
    const isFormValid = isApprove
      ? inbshipRequestDate && expectedDeliveryDate
      : true;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1100,
            width: "50vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "green",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <Stack style={{ padding: "35px", gap: 15 }}>
                  {message1 && <CustomTypography message={message1} />}
                  {message2 && <CustomTypography message={message2} />}
                  {inboundRequest?.status !== getInboundStatusType.pending &&
                    inboundRequest.ExtraUnitsIR && (
                      <span
                        style={{
                          fontFamily: "tmwCustomFont",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "black",
                          letterSpacing: 2,
                          userSelect: "none",
                          textTransform: "uppercase",
                        }}
                      >
                        The Netsuite synchronization process in case of an Extra
                        Units shipment takes longer than for a standard Inbound
                        Request. Please be patient and wait in this page until
                        the process completes (it can take up to 1-2 minutes).
                      </span>
                    )}
                </Stack>
                {isApprove && (
                  <Stack style={{ gap: 20, marginTop: 10 }}>
                    <Stack
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "tmwCustomFont",
                          fontSize: 11,
                          color: "black",
                          letterSpacing: 3,
                          fontWeight: 500,
                          userSelect: "none",
                          textTransform: "uppercase",
                          marginBottom: 5,
                        }}
                      >
                        INBSHIP Request Date *
                      </span>
                      <MobileDatePicker
                        inputFormat="dd/MM/yyyy"
                        value={inbshipRequestDate}
                        onChange={(date) => {
                          if (date) {
                            // Normalize date to noon local time to avoid DST/timezone issues
                            const normalizedDate = new Date(date);
                            normalizedDate.setHours(12, 0, 0, 0);
                            setInbshipRequestDate(normalizedDate);
                          } else {
                            setInbshipRequestDate(date);
                          }
                        }}
                        renderInput={(inputProps) => {
                          return (
                            <>
                              <CustomTextField
                                style={{ width: "100%" }}
                                inputProps={{
                                  value: inputProps.inputProps.value || "",
                                  onClick: inputProps.inputProps.onClick,
                                  endadorment: (
                                    <InputAdornment position="end">
                                      <img
                                        src={CustomCalendarIcon}
                                        style={{
                                          width: "15px",
                                          borderRadius: "3px",
                                        }}
                                        alt="calendar-icon"
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                                calendar
                                error={!inbshipRequestDate}
                                helperText={
                                  !inbshipRequestDate &&
                                  "INBSHIP Request Date is required."
                                }
                                disabled={inboundRequest.ExtraUnitsIR}
                              />
                            </>
                          );
                        }}
                        disabled={inboundRequest.ExtraUnitsIR}
                      />
                    </Stack>
                    <Stack
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "tmwCustomFont",
                          fontSize: 11,
                          color: "black",
                          letterSpacing: 3,
                          fontWeight: 500,
                          userSelect: "none",
                          textTransform: "uppercase",
                          marginBottom: 5,
                        }}
                      >
                        Expected Delivery Date *
                      </span>
                      <MobileDatePicker
                        inputFormat="dd/MM/yyyy"
                        value={expectedDeliveryDate}
                        onChange={(date) => {
                          if (date) {
                            // Normalize date to noon local time to avoid DST/timezone issues
                            const normalizedDate = new Date(date);
                            normalizedDate.setHours(12, 0, 0, 0);
                            setExpectedDeliveryDate(normalizedDate);
                          } else {
                            setExpectedDeliveryDate(date);
                          }
                        }}
                        renderInput={(inputProps) => {
                          return (
                            <>
                              <CustomTextField
                                style={{ width: "100%" }}
                                inputProps={{
                                  value: inputProps.inputProps.value || "",
                                  onClick: inputProps.inputProps.onClick,
                                  endadorment: (
                                    <InputAdornment position="end">
                                      <img
                                        src={CustomCalendarIcon}
                                        style={{
                                          width: "15px",
                                          borderRadius: "3px",
                                        }}
                                        alt="calendar-icon"
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                                calendar
                                error={!expectedDeliveryDate}
                                helperText={
                                  !expectedDeliveryDate &&
                                  "Expected Delivery Date is required."
                                }
                                disabled={inboundRequest.ExtraUnitsIR}
                              />
                            </>
                          );
                        }}
                        disabled={inboundRequest.ExtraUnitsIR}
                      />
                    </Stack>
                    <Stack>
                      <CustomTextField
                        label="Memo"
                        value={approvalMemo || ""}
                        handleChange={(e) => setApprovalMemo(e.target.value)}
                        multiline
                        rows={4}
                        style={{ width: "100%" }}
                      />
                    </Stack>
                  </Stack>
                )}
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                    marginTop: "15px",
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={handleCancel}
                    disabled={submitLoader}
                  />
                  <CustomButton
                    label="Confirm"
                    onClick={handleConfirm}
                    disabled={
                      !inboundRequest.ExtraUnitsIR &&
                      (submitLoader || !isFormValid)
                    }
                    loader={
                      submitLoader && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderDeleteNoteModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1200,
            width: "40vw",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "red",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 15 }}>
                <CustomTypography message="Are you sure you want to delete this note?" />
              </Stack>
              <Stack
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 15,
                  margin: "0 25px 25px 0",
                }}
              >
                <CustomButton
                  label="Cancel"
                  smallButton
                  onClick={() => setDeleteNoteModalVisible(false)}
                />
                <CustomButton
                  label="Confirm"
                  redButton
                  smallButton
                  onClick={async () => {
                    try {
                      await dispatch(
                        thunks.deleteInboundRequestNote(
                          inboundRequest.id,
                          editingNote?.noteId || editingNote?.id,
                        ),
                      );
                      setDeleteNoteModalVisible(false);
                      setEditNoteText("");
                      setEditNoteAttachment(null);
                      setEditNoteAttachmentName("");
                      setEditingNote(null);
                      dispatch(thunks.getInboundRequest(inboundRequest.id));
                      setNotesView("list");
                      toast.success("Note deleted");
                    } catch (e) {
                      toast.error("Failed to delete note");
                    }
                  }}
                  style={{ background: "white" }}
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderCloseIRModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1200,
            width: { xs: "92vw", md: "50vw" },
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background:
                    closeInboundRequestAction === "mark_received"
                      ? "#55aaff"
                      : "black",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <CustomTypography
                  message={
                    closeInboundRequestAction === "mark_received"
                      ? "This action will send a request to Netsuite, but the status change to “Received” will take a while, so please check back after 10-15 minutes."
                      : "Are you sure you want to close this Inbound Request?"
                  }
                />
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={() => {
                      setCloseIRModalOpened(false);
                    }}
                    disabled={closeInboundRequestLoading}
                  />
                  <CustomButton
                    label={
                      closeInboundRequestAction === "mark_received"
                        ? "Mark as Received"
                        : "Close"
                    }
                    onClick={handleCloseInboundRequest}
                    disabled={closeInboundRequestLoading}
                    loader={
                      closeInboundRequestLoading && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderNotifyChangeRequestModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1200,
            width: { xs: "92vw", md: "50vw" },
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "orange",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <CustomTypography message="Notify change request for this Inbound Request" />
                <CustomTextField
                  label="Change requests (required)"
                  value={changeRequestNote}
                  handleChange={(e) => setChangeRequestNote(e.target.value)}
                  multiline
                  minRows={3}
                />
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={() => {
                      setNotifyChangeRequestModalOpened(false);
                      setChangeRequestNote("");
                    }}
                    disabled={notifyChangeRequestLoading}
                  />
                  <CustomButton
                    label="Notify Change Request"
                    onClick={handleNotifyChangeRequest}
                    disabled={
                      notifyChangeRequestLoading || !changeRequestNote?.trim()
                    }
                    loader={
                      notifyChangeRequestLoading && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderExtraUnitsModal = () => {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1200,
            width: { xs: "96vw", md: "90vw" },
            maxHeight: "90vh",
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
                overflow: "hidden",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "#d0d000",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack style={{ padding: "25px", gap: 20 }}>
                <CustomTypography
                  message="Edit Extra Units"
                  title
                  style={{ marginBottom: "10px" }}
                />
                <Stack
                  style={{
                    border: "1px solid #E6E6E6",
                    maxHeight: "55vh",
                    overflow: "auto",
                  }}
                >
                  <Stack
                    style={{
                      flexDirection: "row",
                      fontWeight: "bold",
                      borderBottom: "1px solid #E6E6E6",
                      padding: "8px 6px",
                      fontSize: 10,
                      alignItems: "flex-start",
                      textTransform: "uppercase",
                    }}
                  >
                    <Stack style={{ flex: 1, padding: "0 8px" }}>
                      Internal ID
                    </Stack>
                    <Stack style={{ flex: 2, padding: "0 8px" }}>SKU</Stack>
                    <Stack style={{ flex: 1, padding: "0 8px" }}>
                      EAN Code
                    </Stack>
                    <Stack style={{ flex: 2, padding: "0 8px" }}>
                      Description
                    </Stack>
                    <Stack
                      style={{ flex: 1, padding: "0 8px", textAlign: "right" }}
                    >
                      Pending Qty
                    </Stack>
                    <Stack
                      style={{ flex: 1, padding: "0 8px", textAlign: "right" }}
                    >
                      Ready To Ship
                    </Stack>
                    <Stack
                      style={{ flex: 1, padding: "0 8px", textAlign: "right" }}
                    >
                      Add Extra Units
                    </Stack>
                  </Stack>
                  {selectedPOItems.map((POItem, index) => {
                    const foundItemsQuantity = quantityValues[POItem?.id];

                    return (
                      <Stack
                        key={`extra-units-item-${index}`}
                        style={{
                          flexDirection: "row",
                          fontSize: 13,
                          alignItems: "center",
                          minHeight: "40px",
                          padding: "4px 6px",
                          borderBottom:
                            index !== selectedPOItems.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                        }}
                      >
                        <Stack style={{ flex: 1, padding: "0 8px" }}>
                          {POItem.internalId}
                        </Stack>
                        <Stack style={{ flex: 2, padding: "0 8px" }}>
                          {POItem.itemSku}
                        </Stack>
                        <Stack style={{ flex: 1, padding: "0 8px" }}>
                          {POItem.eanCode}
                        </Stack>
                        <Stack style={{ flex: 2, padding: "0 8px" }}>
                          {POItem.description}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            padding: "0 8px",
                            textAlign: "right",
                          }}
                        >
                          {POItem.remainingQuantity}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            padding: "0 8px",
                            textAlign: "right",
                          }}
                        >
                          {foundItemsQuantity}
                        </Stack>
                        <Stack
                          style={{
                            flex: 1,
                            padding: "0 8px",
                            textAlign: "right",
                            alignItems: "flex-end",
                          }}
                        >
                          <CustomTextField
                            style={{ width: "70px" }}
                            textFieldStyle={{ width: "70px" }}
                            value={extraUnitsReceivedValues[POItem.id] ?? 0}
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            handleChange={(e) => {
                              const inputValue = e.target.value;

                              if (!/^\d*$/.test(inputValue)) {
                                return;
                              }

                              setExtraUnitsReceivedValues((prev) => ({
                                ...prev,
                                [POItem.id]:
                                  inputValue === "" ? 0 : Number(inputValue),
                              }));
                              setExtraUnitsReceivedChanged(true);
                            }}
                          />
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    gap: 15,
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={() => setExtraUnitsModalOpened(false)}
                  />
                  <CustomButton
                    label="SAVE AND CONFIRM"
                    style={{ background: "white" }}
                    onClick={() => {
                      setExtraUnitsModalOpened(false);
                      setExtraUnitsConfirmModalOpened(true);
                    }}
                    disabled={!extraUnitsReceivedChanged}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  const renderMissingItemsChoiceModal = () => {
    const isDistributionBrand =
      inboundRequest?.purchase_order?.brand?.type === "Distribution";
    const canConfirm = isDistributionBrand
      ? true
      : uploadCNForMissingItemsChoice || shipMissingItemsChoice;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(5px)",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1200,
            width: { xs: "96vw", md: "82vw" },
          }}
        >
          <Container maxWidth="sx">
            <Stack
              elevation={12}
              style={{
                background: "white",
                borderRadius: "25px",
                border: "1px solid #E6E6E6",
                maxHeight: "90vh",
                overflow: "hidden",
              }}
            >
              <Stack
                style={{
                  height: "40px",
                  background: "#55eecc",
                  borderRadius: "25px 25px 0 0",
                }}
              />
              <Stack
                style={{
                  padding: "25px",
                  gap: 20,
                  overflowY: "auto",
                  maxHeight: "calc(90vh - 40px)",
                }}
              >
                <Stack style={{ gap: 10 }}>
                  <CustomTypography message="The warehouse has partially received units related to this Inbound Request." />
                  <CustomTypography
                    message={
                      isDistributionBrand
                        ? "Please attach a Credit Note for the missing units."
                        : "You can either upload a Credit Note or create a New Inbound Request for the missing units."
                    }
                  />
                </Stack>
                <Stack
                  style={{
                    border: "1px solid #E6E6E6",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <Stack
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "2fr 1.2fr 0.5fr 1.5fr 1.5fr 0.9fr 0.9fr 0.9fr",
                      gap: 10,
                      padding: "10px 12px",
                      background: "#f5f5f5",
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: "tmwCustomFont",
                    }}
                  >
                    <span>SKU</span>
                    <span>EAN Code</span>
                    <span style={{ textAlign: "right" }}>Unit Cost</span>
                    <span>Colour Description</span>
                    <span>Description</span>
                    <span style={{ textAlign: "right" }}>Expected Qty</span>
                    <span style={{ textAlign: "right" }}>Received Qty</span>
                    <span style={{ textAlign: "right" }}>Missing Units</span>
                  </Stack>
                  <Stack style={{ maxHeight: "240px", overflowY: "auto" }}>
                    {missingItemsRowsForChoiceModal.length > 0 ? (
                      missingItemsRowsForChoiceModal.map((item, index) => (
                        <Stack
                          key={`${item.sku}-${item.eanCode}-${index}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "2fr 1.2fr 0.5fr 1.5fr 1.5fr 0.9fr 0.9fr 0.9fr",
                            gap: 10,
                            padding: "10px 12px",
                            borderTop:
                              index === 0 ? "none" : "1px solid #E6E6E6",
                            fontSize: 11,
                            fontFamily: "tmwCustomFont",
                            alignItems: "center",
                          }}
                        >
                          <span>{item.sku}</span>
                          <span>{item.eanCode}</span>
                          <span style={{ textAlign: "right" }}>
                            {item.unitCost != null ? item.unitCost : "-"}
                          </span>
                          <span>{item.colourDescription}</span>
                          <span>{item.description}</span>
                          <span style={{ textAlign: "right" }}>
                            {item.expectedQty}
                          </span>
                          <span style={{ textAlign: "right" }}>
                            {item.receivedQty}
                          </span>
                          <span style={{ textAlign: "right", fontWeight: 600 }}>
                            {item.missingUnits}
                          </span>
                        </Stack>
                      ))
                    ) : (
                      <Stack
                        style={{
                          padding: "14px 12px",
                          fontSize: 11,
                          fontFamily: "tmwCustomFont",
                          color: "#666",
                        }}
                      >
                        No missing units rows found.
                      </Stack>
                    )}
                  </Stack>
                </Stack>
                {missingItemsRowsForChoiceModal.length > 0 &&
                  (() => {
                    const currencySymbol =
                      inboundRequest?.purchase_order?.currency === "EUR"
                        ? "€"
                        : inboundRequest?.purchase_order?.currency === "GBP"
                          ? "£"
                          : inboundRequest?.purchase_order?.currency === "USD"
                            ? "$"
                            : inboundRequest?.purchase_order?.currency === "JPY"
                              ? "¥"
                              : inboundRequest?.purchase_order?.currency || "";
                    const creditNoteTotal =
                      missingItemsRowsForChoiceModal.reduce(
                        (sum, item) =>
                          sum +
                          (item.unitCost != null
                            ? item.missingUnits * Number(item.unitCost)
                            : 0),
                        0,
                      );
                    return (
                      <Stack
                        style={{
                          flexDirection: "row",
                          justifyContent: "flex-end",
                          fontFamily: "tmwCustomFont",
                          fontSize: 12,
                          fontWeight: 600,
                          paddingRight: 4,
                        }}
                      >
                        {`Credit Note Total: ${creditNoteTotal.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )} ${currencySymbol}`}
                      </Stack>
                    );
                  })()}
                {isDistributionBrand ? (
                  <Stack style={{ marginTop: 4 }}>
                    {renderMissingItemsCreditNoteUploadField(false)}
                  </Stack>
                ) : (
                  <Stack style={{ gap: 5 }}>
                    <Stack
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginLeft: -10,
                      }}
                    >
                      <Checkbox
                        checked={uploadCNForMissingItemsChoice}
                        onChange={(e) =>
                          setUploadCNForMissingItemsChoice(e.target.checked)
                        }
                        style={{
                          color: "black",
                        }}
                      />
                      <CustomTypography
                        message={
                          <>
                            I want to upload a credit note for{" "}
                            <b>some or all</b> of the missing units
                          </>
                        }
                      />
                    </Stack>
                    {uploadCNForMissingItemsChoice && (
                      <Stack style={{ marginLeft: 28, marginTop: 4 }}>
                        {renderMissingItemsCreditNoteUploadField(false)}
                      </Stack>
                    )}
                    <Stack
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginLeft: -10,
                      }}
                    >
                      <Checkbox
                        checked={shipMissingItemsChoice}
                        onChange={(e) =>
                          setShipMissingItemsChoice(e.target.checked)
                        }
                        style={{
                          color: "black",
                        }}
                      />
                      <CustomTypography
                        message={
                          <>
                            I want to ship <b>some or all</b> of the missing
                            units, creating a new Inbound Request for them
                          </>
                        }
                      />
                    </Stack>
                    <Stack style={{ marginTop: 8 }}>
                      <CustomTypography
                        message={
                          <b>
                            Please make sure your choices are correct as you
                            will not be able to change them after you CONFIRM
                            AND SAVE.
                          </b>
                        }
                      />
                    </Stack>
                  </Stack>
                )}
                <Stack
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    marginTop: 10,
                  }}
                >
                  <CustomButton
                    label={
                      isDistributionBrand
                        ? "CONFIRM"
                        : "CONFIRM AND SAVE CHOICES"
                    }
                    onClick={handleConfirmMissingItemsChoice}
                    disabled={!canConfirm || confirmMissingItemsChoiceLoading}
                    loader={
                      confirmMissingItemsChoiceLoading && (
                        <CircularProgress
                          size={20}
                          color="inherit"
                          style={{ marginLeft: "10px" }}
                        />
                      )
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    );
  };

  return (
    <>
      <Seo title="Dashboard: Edit Inbound Request" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {selectPurchaseOrderItemsModalVisible
          ? inboundRequest.status !== getInboundStatusType.approved &&
            inboundRequest.status !== getInboundStatusType.closed &&
            inboundRequest.status !==
              getInboundStatusType.partiallyReceived && (
              <>
                <Stack style={{ gap: 10, marginTop: 10 }}>
                  {renderBackButton()}
                  {isExtraUnitsIR && (
                    <Stack
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CustomTypography
                        message="Extra Units IR"
                        title
                        style={{ padding: "0 25px" }}
                      />
                    </Stack>
                  )}
                  {renderPOInfo()}
                </Stack>
                {renderSelectPurchaseOrderItemsSection()}
              </>
            )
          : !inboundRequestLoading &&
            (inboundRequest ? (
              <Stack
                style={{
                  width: "100%",
                  height: "100%",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <Stack
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    margin: "10px 0",
                  }}
                >
                  <Stack style={{ gap: 10, width: "100%" }}>
                    {renderBackButton()}
                    {isExtraUnitsIR && (
                      <Stack
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CustomTypography
                          message="Extra Units IR"
                          title
                          style={{ padding: "0 25px" }}
                        />
                      </Stack>
                    )}
                    {renderPOInfo()}
                  </Stack>
                  {renderIRActionButtons()}
                  {emailHistoryModalVisible && (
                    <InboundRequestViewEmailHistoryModal
                      title={"Inbound Request Email History"}
                      cancelButtonLabel={"Close"}
                      cancelButton={handleCloseEmailHistoryModal}
                      isOpen={emailHistoryModalVisible}
                      histories={inboundRequestEmailHistory}
                    />
                  )}
                </Stack>
                <Stack style={{ alignItems: "center" }}>
                  {renderPOItems()}
                  <Stack
                    style={{
                      width: "95%",
                      alignItems: "flex-start",
                    }}
                  >
                    <Stack
                      style={{ marginTop: 25, position: "relative" }}
                      sx={{
                        width: { xs: "95%", md: "80%" },
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "25px 50px 25px 25px",
                        overflowX: "auto",
                        overflowY: "hidden",
                        border: "1px solid #E6E6E6",
                      }}
                    >
                      {userGroup === groupTypes.tomorrowCrew &&
                        inboundRequest.status ===
                          getInboundStatusType.authorized && (
                          <Stack
                            style={{ position: "absolute", right: 25, top: 25 }}
                          >
                            <CustomButton
                              label={
                                isEditingAuthorized
                                  ? "Lock Fields"
                                  : "Edit Fields"
                              }
                              style={{ background: "white" }}
                              onClick={() =>
                                setIsEditingAuthorized(!isEditingAuthorized)
                              }
                            />
                          </Stack>
                        )}
                      <Stack
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          width: "100%",
                          height: "100%",
                          gap: 25,
                        }}
                      >
                        {(userGroup === groupTypes.tomorrowCrew ||
                          userGroup === groupTypes.brand ||
                          userGroup === groupTypes.supplier) &&
                          renderInvoiceNetTotalSection()}
                        {renderInputSelect(
                          packingListFileData
                            ? "Packing List Document"
                            : "Upload Packing List Document",
                          packingListFileName,
                          "Packing List/" + packingListFileName,
                          setPackingListFileName,
                          packingListFileData,
                          setPackingListFileData,
                          ["pdf", "xls", "xlsx", "doc", "docx"],
                          !!packingListFileError,
                          packingListFileError,
                        )}
                        {renderInputSelect(
                          invoiceFileData ? "Invoice" : "Upload Invoice",
                          invoiceFileName,
                          "Invoice/" + invoiceFileName,
                          setInvoiceFileName,
                          invoiceFileData,
                          setInvoiceFileData,
                          ["pdf", "xls", "xlsx", "doc", "docx"],
                          !!invoiceFileError,
                          invoiceFileError,
                        )}
                        <Stack style={{ flexDirection: "row" }}>
                          {shouldShowMissingItemsCreditNoteField && (
                            <Stack style={{ width: "100%", gap: 5 }}>
                              {/* <CustomTypography
                              message="3PL Credit Note"
                              style={{ fontSize: 12 }}
                            /> */}
                              {renderMissingItemsCreditNoteUploadField(true)}
                            </Stack>
                          )}
                          {shouldShowMissingItemsChoicesSummary &&
                            renderMissingItemsChoicesSummary()}
                        </Stack>
                      </Stack>
                      {notesModalVisible &&
                        inboundRequest.status !==
                          getInboundStatusType.approved &&
                        inboundRequest.status !== getInboundStatusType.closed &&
                        renderNotesModal()}
                      {notesSystemModalVisible && renderNotesSystemModal()}
                      {historyModalVisible &&
                        (userGroup === groupTypes.tomorrowCrew ||
                          (userGroup === groupTypes.brand &&
                            inboundRequest?.purchase_order?.brand?.type ===
                              "Invested")) &&
                        renderHistoryModal()}
                      {confirmIRModalOpened &&
                        (userGroup === groupTypes.tomorrowCrew ||
                          userGroup === groupTypes.brand ||
                          userGroup === groupTypes.supplier) &&
                        renderConfirmIRModal(
                          () => handleSubmitInboundRequest(),
                          () => setConfirmIRModalOpened(false),
                          `Are you sure you want to Submit this Inbound Request? You will not be able to edit it anymore.`,
                          inboundRequestStatusChangeSubmitLoading,
                        )}
                      {extraUnitsConfirmModalOpened &&
                        renderConfirmIRModal(
                          () => handleSaveAndConfirmExtraUnits(),
                          () => setExtraUnitsConfirmModalOpened(false),
                          `Do you confirm that all the Extra Units received have been saved correctly? This action will generate a new Inbound Request for the extra units and will inform the brand/supplier about this, asking them to upload the invoice and packing list and to submit the new Inbound Request.`,
                          saveChangesLoading,
                        )}
                      {approveIRModalOpened &&
                        (userGroup === groupTypes.tomorrowCrew ||
                          userGroup === groupTypes.brand) &&
                        renderApproveIRModal(
                          () => handleApproveInboundRequest(),
                          () => setApproveIRModalOpened(false),
                          inboundRequest?.purchase_order?.brand?.type ===
                            "Invested" &&
                            inboundRequest?.status ===
                              getInboundStatusType.pending
                            ? "Are you sure you want to authorize this Inbound Request?"
                            : "Are you sure you want to approve this Inbound Request?",
                          inboundRequest?.purchase_order?.brand?.type ===
                            "Invested" &&
                            inboundRequest?.status ===
                              getInboundStatusType.pending ? (
                            <span>
                              {`You will not be able to edit it anymore. After you
                              press on "Authorize" the Inbound Request status
                              will be changed to "Authorized" until a Tomorrow
                              Crew user will review it.`}
                            </span>
                          ) : (
                            <>
                              <span style={{ fontWeight: 600 }}>
                                NS Transaction Status:{" "}
                              </span>
                              {
                                inboundRequest?.purchase_order
                                  ?.nsTransactionStatus
                              }
                            </>
                          ),
                          inboundRequestStatusChangeApproveLoading,
                          !(
                            inboundRequest?.purchase_order?.brand?.type ===
                              "Invested" &&
                            inboundRequest?.status ===
                              getInboundStatusType.pending
                          ),
                        )}
                    </Stack>
                  </Stack>
                  {renderPickupDetailsSection()}
                </Stack>
              </Stack>
            ) : (
              <Stack
                style={{
                  height: "90%",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CustomTypography
                  message="This Inbound Request does not exist or has been deleted."
                  title
                />
              </Stack>
            ))}
      </Box>
      {missingItemsChoiceModalVisible && renderMissingItemsChoiceModal()}
      {extraUnitsModalOpened && renderExtraUnitsModal()}
      {notifyChangeRequestModalOpened && renderNotifyChangeRequestModal()}
      {closeIRModalOpened && renderCloseIRModal()}
      {deleteNoteModalVisible && renderDeleteNoteModal()}
    </>
  );
};

export default withInboundProcessGuard(EditInboundRequestPage);
